import { useRef, useMemo, useEffect, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import './editor.css';
import { useDebounce } from '../hooks/useDebounce';
import EditorSidebarSection from '../components/shared/EditorSidebarSection';
import EditorSectionPreviewModal from '../components/shared/EditorSectionPreviewModal';
import EditorTopBar from '../components/EditorTopBar';
import CitationCard from '../components/CitationCard';
import { useSearchParams, useParams, useBlocker } from 'react-router-dom';
import Button from '../components/shared/Button';
import AddCitationModal from '../components/modals/add-citation/addCitationModal';
import ConfirmationModal from '../components/modals/confirmationModal';
import { ChevronUp, AlertTriangle } from 'lucide-react';
import { DocumentService } from '../services/DocumentService';
import { CitationService } from '../services/CitationService';
import {
	mapBackendToFrontend,
	mapBackendToFormFields,
	getFrontendSourceType
} from '../utils/citationFieldMapper';
import { generateInText } from '../utils/citationGenerator';
import { countCitationIdInDocument } from '../utils/citationDocumentMarkers';
import {
	buildCitationInsertPayload,
	stripInvisibleText,
	stripPastedInlineFontStyles,
	sortReferenceEntryParagraphs
} from '../utils/citationInsertBuilder';
import {
	ensureReferencesSectionHeading,
	handleEditorExternalLinkClick
} from '../utils/referenceOutputUtils';
import { formatLocatorValue } from '../utils/citationLocatorHelper';
import { Toast } from '../components/shared/Toast';
import Loader from '../components/shared/Loader';
import { useAuthContext } from '../components/AuthProvider';
import { QueryService } from '../services/QueryService';
import TextareaWithCounter from '../components/shared/TextareaWithCounter';
import DeleteIcon from '../../src/assets/icons/delete.svg';
import CitationDetailModal from '../components/modals/add-citation/citationDetailModal';
import Tooltip from '../components/shared/Tooltip';
import { generateCitationForDisplay } from '../utils/citationDisplayHelper';
import PDFPreviewModal from '../components/modals/PDFPreviewModal';
import PDFExportOptionsModal from '../components/modals/PDFExportOptionsModal';
import InsertChartModal from '../components/modals/InsertChartModal';
import { API_BASE_URL } from '../utils/constants';
import axiosInstance from '../services/axiosInstance';
import { getDefaultExportConfig, resolveRomanNumeralFrontPages } from '../utils/pdfExportUtils';
import { exportDocumentToPdf } from '../services/pdfExportService';
import { exportDocumentToWord } from '../services/wordExportService';
import { attachEditorSpellcheck } from '../utils/editorSpellcheck';
import { useSubscription } from '../hooks/useSubscription';
import { getPlanFeatures, canExportFormat } from '../services/SubscriptionService';
import LimitReachedModal, { isLimitReachedError } from '../components/modals/LimitReachedModal';
import { WatermarkService } from '../services/WatermarkService';
import { FEATURE_WATERMARK_REMOVAL } from '../config/features';
import {
	buildApaComplianceParagraphHtml,
	getApaComplianceSectionGuideHtml,
	getApaComplianceStatementText
} from '../utils/apaCompliance';
import {
	getDissertationSectionGuideHtml,
	getSectionRestoreHtml,
	matchDissertationChapter1Key,
	splitDissertationGuideHtml
} from '../utils/dissertationChapter1TemplateData';
import {
	fileContentUsesCategories,
	buildSidebarSectionsFromFileContent,
	getTopLevelSections,
	parseFileContent,
	parseVirtualH2NavId,
	resolveOutlineIdFromNode,
	resolveActiveOutlineIdFromElement,
	resolveActiveOutlineIdFromScroll,
	resolveDomElementForNavId,
	getEditorScrollTargets,
} from '../utils/fileContentStructureUtils';
import {
	blobToPngBlob,
	clearEmptyBoldContainers,
	normalizePastedLists,
	stripPasteArtifacts,
	svgDataUrlToPngDataUrl,
	unwrapBlockBoldWhenEmptySelection,
} from '../utils/editorContentNormalize';


// TinyMCE API Key from environment (Create React App requires REACT_APP_ prefix)
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const SECTION_GUIDE_INTRO_HTML = `<p class="wf-section-guide-intro">WriteForm keeps this section aligned to dissertation structure and APA rules. Use this guide to draft with confidence.</p>`;
const CHART_COLORS = ['#4f46e5', '#14b8a6', '#f97316', '#e11d48', '#0891b2', '#65a30d'];

const escapeHtml = (value = '') =>
	String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const buildChartSvgDataUrl = ({ chartType, title, rows }) => {
	const width = 860;
	const height = 500;
	const m = { top: 70, right: 40, bottom: 90, left: 70 };
	const chartW = width - m.left - m.right;
	const chartH = height - m.top - m.bottom;
	const maxV = Math.max(...rows.map((r) => Number(r.value) || 0), 1);

	const axis = `
		<line x1="${m.left}" y1="${m.top + chartH}" x2="${m.left + chartW}" y2="${m.top + chartH}" stroke="#1f2937" stroke-width="2"/>
		<line x1="${m.left}" y1="${m.top}" x2="${m.left}" y2="${m.top + chartH}" stroke="#1f2937" stroke-width="2"/>
	`;

	let content = '';
	if (chartType === 'pie') {
		const cx = m.left + chartW / 2;
		const cy = m.top + chartH / 2 + 8;
		const r = Math.min(chartW, chartH) * 0.34;
		const total = rows.reduce((sum, r0) => sum + Math.max(0, Number(r0.value) || 0), 0) || 1;
		let start = -Math.PI / 2;
		content += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#f8fafc" stroke="#e2e8f0"/>`;
		rows.forEach((row, i) => {
			const val = Math.max(0, Number(row.value) || 0);
			const angle = (val / total) * Math.PI * 2;
			const end = start + angle;
			const x1 = cx + r * Math.cos(start);
			const y1 = cy + r * Math.sin(start);
			const x2 = cx + r * Math.cos(end);
			const y2 = cy + r * Math.sin(end);
			const large = angle > Math.PI ? 1 : 0;
			const color = CHART_COLORS[i % CHART_COLORS.length];
			content += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z" fill="${color}" />`;
			start = end;
		});
		rows.forEach((row, i) => {
			const y = m.top + 8 + i * 20;
			const color = CHART_COLORS[i % CHART_COLORS.length];
			content += `<rect x="${m.left + chartW - 180}" y="${y}" width="12" height="12" fill="${color}" rx="2"/>`;
			content += `<text x="${m.left + chartW - 162}" y="${y + 10}" font-size="12" fill="#111827">${escapeHtml(
				row.label
			)}</text>`;
		});
	} else if (chartType === 'line') {
		content += axis;
		const points = rows
			.map((row, i) => {
				const x = m.left + (i * chartW) / Math.max(rows.length - 1, 1);
				const y = m.top + chartH - ((Number(row.value) || 0) / maxV) * chartH;
				return { x, y, label: row.label, value: row.value };
			})
			.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
		content += `<polyline fill="none" stroke="#4f46e5" stroke-width="3" points="${points
			.map((p) => `${p.x},${p.y}`)
			.join(' ')}" />`;
		points.forEach((p, i) => {
			const color = CHART_COLORS[i % CHART_COLORS.length];
			content += `<circle cx="${p.x}" cy="${p.y}" r="4.5" fill="${color}" />`;
			content += `<text x="${p.x}" y="${m.top + chartH + 18}" text-anchor="middle" font-size="12" fill="#374151">${escapeHtml(
				p.label
			)}</text>`;
		});
	} else {
		content += axis;
		const barGap = 12;
		const barW = Math.max(20, (chartW - barGap * (rows.length + 1)) / Math.max(rows.length, 1));
		rows.forEach((row, i) => {
			const value = Number(row.value) || 0;
			const h = Math.max(2, (value / maxV) * chartH);
			const x = m.left + barGap + i * (barW + barGap);
			const y = m.top + chartH - h;
			const color = CHART_COLORS[i % CHART_COLORS.length];
			content += `<rect x="${x}" y="${y}" width="${barW}" height="${h}" fill="${color}" rx="5"/>`;
			content += `<text x="${x + barW / 2}" y="${m.top + chartH + 18}" text-anchor="middle" font-size="12" fill="#374151">${escapeHtml(
				row.label
			)}</text>`;
		});
	}

	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
		<rect width="100%" height="100%" fill="#ffffff"/>
		<text x="${width / 2}" y="38" text-anchor="middle" font-size="24" font-family="Arial, sans-serif" fill="#111827">${escapeHtml(
			title || 'Chart'
		)}</text>
		${content}
	</svg>`;
	return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const DocumentEditor = () => {
	const { user } = useAuthContext();
	const { getEffectivePlanName } = useSubscription();
	const [initialContent, setInitialContent] = useState('');
	const [editorReady, setEditorReady] = useState(false);
	const [doc, setDoc] = useState(null);
	const [cursorSectionId, setCursorSectionId] = useState(null);
	const [exportEntitlements, setExportEntitlements] = useState(null);
	const [loading, setLoading] = useState(false);
	const [sidebarSections, setSidebarSections] = useState([]);
	useEffect(() => {
		sidebarSectionsRef.current = sidebarSections;
		if (!editorReady || !sidebarSections.length) return;
		const editor = editorRef.current;
		if (editor) {
			syncOutlineHighlightFromEditor(editor, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorReady, sidebarSections]);
	const [query, setQuery] = useState('');
	const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);
	// const [isExporting, setIsExporting] = useState(false);
	const [citations, setCitations] = useState([]);
	const [selectedCitation, setSelectedCitation] = useState(null);
	const [isSourceLibraryExpanded, setIsSourceLibraryExpanded] = useState(true);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDeletingCitation, setIsDeletingCitation] = useState(false);
	const [citationToDelete, setCitationToDelete] = useState(null);
	const [editingCitation, setEditingCitation] = useState(null);
	const [citationModalMode, setCitationModalMode] = useState('add'); // "add" | "edit" | "insert"
	const [citationToInsert, setCitationToInsert] = useState(null);
	const [citationInsertOccurrenceCount, setCitationInsertOccurrenceCount] = useState(0);
	const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
	const [viewingCitationDetails, setViewingCitationDetails] = useState(null);
	const [savedCursorPosition, setSavedCursorPosition] = useState(null);
	const [isPdfPreviewLoading, setIsPdfPreviewLoading] = useState(false);
	const [isPdfExportLoading, setIsPdfExportLoading] = useState(false);
	const [isWordExportLoading, setIsWordExportLoading] = useState(false);
	const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);
	const [pdfBlobForPreview, setPdfBlobForPreview] = useState(null);
	const [savingStatus, setSavingStatus] = useState('saved'); // 'saved', 'saving', 'error'
	const [manualSavingStatus, setManualSavingStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
	const [isExportOptionsModalOpen, setIsExportOptionsModalOpen] = useState(false);
	const [exportConfig, setExportConfig] = useState(null); // To persist user choices during session
	const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
	const [supportMessage, setSupportMessage] = useState('');
	const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
	const [isChartModalOpen, setIsChartModalOpen] = useState(false);
	const [limitModalOpen, setLimitModalOpen] = useState(false);
	const [limitPayload, setLimitPayload] = useState(null);

	const [sectionPreviewOpen, setSectionPreviewOpen] = useState(false);
	const [sectionPreviewTitle, setSectionPreviewTitle] = useState('');
	const [sectionPreviewSubtitle, setSectionPreviewSubtitle] = useState('');
	const [sectionPreviewHtml, setSectionPreviewHtml] = useState('');
	const [sectionPreviewVariant, setSectionPreviewVariant] = useState('guide');
	const [sectionGuideExampleHtml, setSectionGuideExampleHtml] = useState(null);

	// Refs for image tracking
	const deletedImageUrls = useRef(new Set());
	const currentImageUrls = useRef(new Set());
	const docRef = useRef(doc);
	const lastReferencesEditWarningAtRef = useRef(0);

	const editorRef = useRef(null);
	const sidebarSectionsRef = useRef([]);
	const outlineScrollRafRef = useRef(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const { id: documentId } = useParams();
	const isCreated = searchParams.get('isCreated') === 'true';
	const [showCitationManager, setShowCitationManager] = useState(isCreated);

	// Track if initial content has been loaded and rendered
	const isInitialLoadComplete = useRef(false);

	// Store previous content state for change detection (reconciliation)
	// Map<sectionId, {content: string, subsections: Map<subId, string>}>
	const previousContentRef = useRef(new Map());

	// Document Information
	const documentTitle = doc?.title || '';
	const documentType = doc?.documentType?.name || '';
	const documentStatus = doc?.status || '';
	const documentEditedTime = doc?.updatedAt
		? new Date(doc.updatedAt).toISOString().slice(0, 10)
		: '';
	const documentFormat = doc?.format?.name || '';
	const documentUniversity = doc?.university?.name || '';

	const isGraduateDoc = useMemo(() => {
		const n = (documentType || '').toLowerCase();
		return /dissertation|thesis|ph\.?\s*d/.test(n);
	}, [documentType]);

	// SOW rule: force new-page major sections for Dissertation/Master's Thesis and Professional academic papers.
	// Student academic papers keep continuous sections unless imported from a formatted Word doc.
	const shouldForceSectionPageBreak = useMemo(() => {
		if (doc?.fileContent?.meta?.importFormatted === true) return true;
		if (doc?.fileContent?.meta?.importDraftCleaned === true) return true;
		const n = (documentType || '').toLowerCase();
		if (/dissertation|master'?s\s*thesis|masters\s*thesis|honors\s*thesis/.test(n)) return true;
		if ((/professional|manuscript/.test(n)) && (/paper|academic|manuscript/.test(n))) return true;
		return false;
	}, [documentType, doc?.fileContent?.meta?.importFormatted, doc?.fileContent?.meta?.importDraftCleaned]);

	// Plan & watermark state
	const effectivePlan = getEffectivePlanName();
	const planFeatures = getPlanFeatures(effectivePlan);
	const planHasWatermark = planFeatures?.hasWatermark ?? true;
	const hasWatermarkRemoval = exportEntitlements?.hasWatermarkRemoval === true;
	const hasWatermarkForExport = planHasWatermark && !hasWatermarkRemoval;
	const isEssentialsPlan = effectivePlan === 'essentials';
	const canExportWord = canExportFormat(effectivePlan, 'DOCX');

	// Filter citations based on search query
	const filteredCitations = useMemo(() => {
		if (!query.trim()) return citations;

		const searchTerm = query.toLowerCase().trim();

		return citations.filter((citation) => {
			// Search in title
			if (citation.title?.toLowerCase().includes(searchTerm)) return true;

			// Search in various title fields
			if (citation.bookTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.articleTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.chapterTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.episodeTitle?.toLowerCase().includes(searchTerm)) return true;

			// Search in authors
			if (citation.authors && Array.isArray(citation.authors)) {
				const authorMatch = citation.authors.some((author) => {
					if (typeof author === 'string') {
						return author.toLowerCase().includes(searchTerm);
					}
					if (author.name) {
						return author.name.toLowerCase().includes(searchTerm);
					}
					return false;
				});
				if (authorMatch) return true;
			}

			// Search in organization
			if (citation.organization?.toLowerCase().includes(searchTerm)) return true;

			// Search in year
			if (citation.year?.includes(searchTerm)) return true;

			// Search in category and sourceType
			if (citation.category?.toLowerCase().includes(searchTerm)) return true;
			if (citation.sourceType?.toLowerCase().includes(searchTerm)) return true;

			// Search in other name fields
			if (citation.communicatorName?.toLowerCase().includes(searchTerm)) return true;
			if (citation.uploaderOrAuthor?.toLowerCase().includes(searchTerm)) return true;
			if (citation.authorName?.toLowerCase().includes(searchTerm)) return true;

			return false;
		});
	}, [citations, query]);

	const handleEditDocumentClick = () => {
		setSearchParams({ isCreated: true }, { replace: true });
		setShowCitationManager(true);
	};

	function buildHtmlFromFileContent(fileContent) {
		let html = '';
		let isFirstSection = true;

		const fixSmashedListParagraphs = (sectionHtml) => {
			if (!sectionHtml || typeof sectionHtml !== 'string') return sectionHtml || '';
			try {
				const parser = new DOMParser();
				const doc = parser.parseFromString(`<div id="wf-root">${sectionHtml}</div>`, 'text/html');
				const root = doc.getElementById('wf-root');
				if (!root) return sectionHtml;
				root.querySelectorAll('p, div').forEach((el) => {
					if (el.querySelector('p,div,table,ul,ol,h1,h2,h3')) return; // only leaf-ish blocks
					const text = String(el.textContent || '')
						.replace(/\u00a0/g, ' ')
						.replace(/\s+/g, ' ')
						.trim();
					if (!/^(Table|Figure)\s*\d+/i.test(text)) return;
					const parts = text
						.split(/(?=(?:Table|Figure)\s*\d+)/i)
						.map((s) => s.trim())
						.filter(Boolean);
					if (parts.length < 2) return;
					const frag = doc.createDocumentFragment();
					parts.forEach((part) => {
						const np = doc.createElement('p');
						np.className = 'wf-no-indent';
						np.setAttribute('style', 'text-indent:0;margin:0');
						np.textContent = part;
						frag.appendChild(np);
					});
					el.replaceWith(frag);
				});
				return root.innerHTML;
			} catch {
				return sectionHtml;
			}
		};

		const renderSection = (section) => {
			if (section.isVisible === false) return;
			const titleKey = String(section.title || '').trim().toLowerCase();
			const isKeywordsSection = titleKey === 'keywords';
			const pageBreakClass =
				!isFirstSection && shouldForceSectionPageBreak && !isKeywordsSection
					? 'page-break-before'
					: '';
			isFirstSection = false;
			const bodyHtml = fixSmashedListParagraphs(section.content?.html || '');
			html += `<div id="${section.id}" class="${pageBreakClass}">${bodyHtml}`;
			(section.subsections || []).forEach((sub) => {
				if (sub.isVisible === false) return;
				if (!(sub.optional === false || sub.optional === undefined)) return;
				html += `<div id="${sub.id}">${fixSmashedListParagraphs(sub.content?.html || '')}</div>`;
			});
			html += `</div>`;
		};

		// Handle category-based structure (skip when categories is empty — use sections)
		if (fileContentUsesCategories(fileContent)) {
			fileContent.categories.forEach((category) => {
				(category.sections || []).forEach((section) => {
					if (section.isVisible === false) return;
					// Match flat-section behavior: visible sections render (optional add-ins stay hidden via isVisible)
					if (section.optional === true && section.required !== true && section.isVisible !== true) {
						return;
					}
					renderSection(section);
				});
			});
		} else if (fileContent.sections && Array.isArray(fileContent.sections)) {
			fileContent.sections.forEach((section) => {
				renderSection(section);
			});
		}

		return html;
	}

	/** Reorder top-level section divs in the editor to match category/section order in fileContent. */
	function syncEditorSectionOrderFromFileContent(editor, fileContent) {
		if (!editor || !fileContent?.categories) return;
		const body = editor.getBody();
		if (!body) return;
		const orderedIds = [];
		fileContent.categories.forEach((category) => {
			(category.sections || []).forEach((section) => {
				if (section.optional === false && section.isVisible !== false) {
					orderedIds.push(section.id);
				}
			});
		});
		if (!orderedIds.length) return;
		const frag = document.createDocumentFragment();
		for (const id of orderedIds) {
			const el = body.querySelector(`#${CSS.escape(id)}`);
			if (el) frag.appendChild(el);
		}
		if (!frag.childNodes.length) return;
		body.insertBefore(frag, body.firstChild);
	}

	// Fetch Document Data
	// Fetch Document Data
	useEffect(() => {
		if (!documentId) return;
		let mounted = true;

		// Reset the flag when loading a new document
		isInitialLoadComplete.current = false;

		(async () => {
			try {
				setLoading(true);
				const res = await DocumentService.fetchDocumentById(documentId);
				const d = res?.document;
				if (!mounted || !d) return;

				setDoc(d);
				docRef.current = d;
				if (res?.exportEntitlements) {
					setExportEntitlements(res.exportEntitlements);
				} else {
					setExportEntitlements(null);
				}

				// Load citations from document (newest first)
				if (d?.citations && Array.isArray(d.citations)) {
					const transformedCitations = d.citations
						.map((citation) => mapBackendToFrontend(citation))
						.sort((a, b) => {
							const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
							const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
							return bTime - aTime; // Newest first
						});
					setCitations(transformedCitations);
				} else {
					setCitations([]);
				}

				const fileContent = parseFileContent(d?.fileContent);
				if (fileContent) {
					const html = buildHtmlFromFileContent(fileContent);
					setInitialContent(html);
					setSidebarSections(
						buildSidebarSectionsFromFileContent(fileContent, d.title)
					);
					const docTypeName = d?.documentType?.name || '';
					setExportConfig(getDefaultExportConfig(docTypeName));
					if (!html?.trim()) {
						console.warn(
							'Document fileContent has no renderable sections',
							fileContent
						);
					}
				}
			} catch (e) {
				const message = e?.response?.data?.message || e?.message || 'Failed to load document';
				Toast.error(message);
			} finally {
				if (mounted) setLoading(false);
			}
		})();

		return () => {
			mounted = false;
		};
	}, [documentId]);

	// Prevent background scrolling when modals are open
	useEffect(() => {
		if (isCitationModalOpen || isViewDetailsModalOpen || isPDFPreviewOpen || isDeleteModalOpen || sectionPreviewOpen) {
			// Disable scrolling on body and main element
			const mainEl = document.querySelector('main');
			const originalBodyOverflow = document.body.style.overflow;
			const originalMainOverflow = mainEl ? mainEl.style.overflow : '';

			document.body.style.overflow = 'hidden';
			if (mainEl) {
				mainEl.style.overflow = 'hidden';
			}

			// Re-enable scrolling when modal closes
			return () => {
				document.body.style.overflow = originalBodyOverflow;
				if (mainEl) {
					mainEl.style.overflow = originalMainOverflow;
				}
			};
		}
	}, [isCitationModalOpen, isViewDetailsModalOpen, isPDFPreviewOpen, isDeleteModalOpen, sectionPreviewOpen]);

	// Initialize previous content ref for change detection (reconciliation baseline)
	// IMPORTANT: Must use editor's normalized output, not raw HTML, because TinyMCE modifies HTML on load
	const initializePreviousContentFromEditor = () => {
		const editor = editorRef.current;
		if (!editor) return;

		// Get the normalized HTML from the editor (this is what we'll compare against)
		const editorHTML = editor.getContent();
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = editorHTML;

		const sectionDivs = tempDiv.querySelectorAll('[id^="section-"]');
		previousContentRef.current.clear();

		sectionDivs.forEach((sectionDiv) => {
			const sectionId = sectionDiv.getAttribute('id');
			// Match both regular sections (section-27) and added sections (section-27-added-1234567890)
			if (!sectionId || !sectionId.match(/^section-\d+(-added-\d+)?$/)) return;

			// Extract section number (works for both section-27 and section-27-added-123)
			const sectionNumber = sectionId.split('-')[1];
			const sectionClone = sectionDiv.cloneNode(true);

			// For added sections, subsections also have the -added- suffix, so we need to match by prefix
			// Get all subsections that belong to this section (either subsection-27-X or subsection-27-X-added-Y)
			const subsectionDivs = sectionClone.querySelectorAll(`[id^="subsection-${sectionNumber}-"]`);
			const subsectionsMap = new Map();

			subsectionDivs.forEach((subDiv) => {
				const subId = subDiv.getAttribute('id');
				if (subId) {
					subsectionsMap.set(subId, subDiv.innerHTML.trim());
				}
				subDiv.remove();
			});

			previousContentRef.current.set(sectionId, {
				content: sectionClone.innerHTML.trim(),
				subsections: subsectionsMap
			});
		});

		console.log('📋 Initialized change detection baseline with', previousContentRef.current.size, 'sections');
	};

	// Set initial content when editor is ready and content is loaded
	useEffect(() => {
		if (!editorRef.current || !initialContent || !editorReady) return;

		// Only set content once on initial load
		if (!isInitialLoadComplete.current) {
			const editor = editorRef.current;
			editor.setContent(initialContent);

			// Clear undo history so the user can't undo back to the empty state
			// This makes the loaded content the "starting point"
			editor.undoManager.clear();

			// Wait for TinyMCE to fully process/normalize the content, then initialize baseline
			// This ensures we compare against TinyMCE's normalized output, not the raw HTML
			setTimeout(() => {
				initializePreviousContentFromEditor();

				isInitialLoadComplete.current = true;

				// Insert APA compliance paragraph when enabled (not a TOC section; body-only)
				const d = docRef.current;
				if (d?.finalFormatSettings?.includeApaComplianceStatement && editorRef.current) {
					const body = editorRef.current.getBody();
					if (!body.querySelector('#wf-apa-compliance-statement')) {
						const dtName = d?.documentType?.name || '';
						body.insertAdjacentHTML('beforeend', buildApaComplianceParagraphHtml(dtName));
					}
				}

				// Set editor editable state after content is fully loaded
				if (editorRef.current) {
					if (isCreated) {
						editorRef.current.mode.set('design');
					} else {
						editorRef.current.mode.set('readonly');
					}
				}
			}, 200); // Slightly longer delay to ensure TinyMCE finishes processing
		}
	}, [initialContent, editorReady, isCreated]);

	useEffect(() => {
		if (doc) {
			docRef.current = doc;
		}
	}, [doc]);

	// Enable/Disable editor based on isCreated state changes (after initial load)
	useEffect(() => {
		// Only run this if initial load is complete
		if (!isInitialLoadComplete.current) return;
		if (!editorRef.current) return;

		if (isCreated) {
			editorRef.current.mode.set('design');
		} else {
			editorRef.current.mode.set('readonly');
		}
	}, [isCreated]);

	// Function to build updated document data from TinyMCE editor content
	// Implements reconciliation - only updates sections/subsections that have changed
	const buildUpdatedDocumentData = () => {
		try {
			const editor = editorRef.current;
			if (!editor) return null;

			const editorHTML = editor.getContent();

			// Create a temporary div to parse the HTML
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = editorHTML;

			// Find all section divs by ID pattern (section-X)
			const sectionDivs = tempDiv.querySelectorAll('[id^="section-"]');

			// Map to store extracted content by section ID
			const extractedSections = new Map();

			// Track what changed (reconciliation)
			const changes = {
				hasChanges: false,
				changedSections: [] // [{sectionId, contentChanged, changedSubsections: []}]
			};

			sectionDivs.forEach((sectionDiv) => {
				const sectionId = sectionDiv.getAttribute('id');
				// Match both regular sections (section-27) and added sections (section-27-added-1234567890)
				if (!sectionId || !sectionId.match(/^section-\d+(-added-\d+)?$/)) return;

				// Extract section number (works for both section-27 and section-27-added-123)
				const sectionNumber = sectionId.split('-')[1];

				// Clone the section div to work with
				const sectionClone = sectionDiv.cloneNode(true);

				// Find all subsections within this section
				// For added sections, subsections also have the -added- suffix
				const subsectionDivs = sectionClone.querySelectorAll(`[id^="subsection-${sectionNumber}-"]`);
				const subsections = [];
				const subsectionsMap = new Map(); // For change detection

				subsectionDivs.forEach((subDiv) => {
					const subId = subDiv.getAttribute('id');
					if (!subId) return;

					const subContent = subDiv.innerHTML.trim();

					// Get the innerHTML of the subsection (its content)
					subsections.push({
						id: subId,
						content: subContent
					});

					// Store in map for change detection
					subsectionsMap.set(subId, subContent);

					// Remove subsection from clone to get section-only content
					subDiv.remove();
				});

				// Sort subsections by their number (handle both regular and -added- patterns)
				subsections.sort((a, b) => {
					const aNum = parseInt(a.id.split('-')[2]);
					const bNum = parseInt(b.id.split('-')[2]);
					return aNum - bNum;
				});

				// What remains in sectionClone is section content (without subsections)
				const sectionContent = sectionClone.innerHTML.trim();

				extractedSections.set(sectionId, {
					content: sectionContent,
					subsections: subsections
				});

				// --- CHANGE DETECTION (Reconciliation) ---
				const previousSection = previousContentRef.current.get(sectionId);

				// Check if section content changed (direct string comparison - 100% accurate)
				const sectionContentChanged = !previousSection || previousSection.content !== sectionContent;

				// Check which subsections changed
				const changedSubsections = [];
				subsections.forEach((sub) => {
					const prevSubContent = previousSection?.subsections?.get(sub.id);
					if (prevSubContent !== sub.content) {
						changedSubsections.push(sub.id);
					}
				});

				// Record changes if any
				if (sectionContentChanged || changedSubsections.length > 0) {
					changes.hasChanges = true;
					changes.changedSections.push({
						sectionId,
						contentChanged: sectionContentChanged,
						changedSubsections
					});
				}

				// Update previous content ref for next comparison
				previousContentRef.current.set(sectionId, {
					content: sectionContent,
					subsections: subsectionsMap
				});
			});

			// If no changes detected, return null to skip update
			if (!changes.hasChanges) {
				console.log('📋 No changes detected, skipping update');
				return null;
			}

			console.log('📝 Changes detected:', changes.changedSections.map(c => ({
				section: c.sectionId,
				contentChanged: c.contentChanged,
				subsectionsChanged: c.changedSubsections.length
			})));

			// Check if using category-based structure (non-empty categories array)
			if (fileContentUsesCategories(doc?.fileContent)) {
				// Build updated categories - only update changed sections
				const updatedCategories = doc.fileContent.categories.map((category) => {
					const updatedSections = category.sections.map((section) => {
						// Check if this section has any changes
						const sectionChange = changes.changedSections.find(c => c.sectionId === section.id);

						if (!sectionChange) {
							return section; // No changes, return as-is
						}

						const extracted = extractedSections.get(section.id);
						if (!extracted) return section;

						// Update subsections - only the ones that changed
						const updatedSubsections = (section.subsections || []).map((sub) => {
							// Check if this specific subsection changed
							if (!sectionChange.changedSubsections.includes(sub.id)) {
								return sub; // No change, return as-is
							}

							const extractedSub = extracted.subsections.find(s => s.id === sub.id);
							const subHtml = extractedSub?.content || sub.content?.html || '';
							const updatedTitle = (() => {
								const temp = document.createElement('div');
								temp.innerHTML = subHtml;
								const h2 = temp.querySelector('h2');
								const h2Text = (h2?.textContent || '').trim();
								return h2Text || sub.title;
							})();

							return {
								...sub,
								title: updatedTitle,
								content: {
									html: subHtml
								}
							};
						});

						return {
							...section,
							// Only update section content if it changed
							content: sectionChange.contentChanged
								? { html: extracted.content }
								: section.content,
							subsections: updatedSubsections
						};
					});

					return {
						...category,
						sections: updatedSections
					};
				});

				return {
					...doc,
					fileContent: {
						...doc.fileContent,
						categories: updatedCategories
					},
					_changes: changes // Include change metadata
				};
			}
			// Fallback for old structure
			else {
				const sections = [];

				extractedSections.forEach((extracted, sectionId) => {
					// Check if this section has changes
					const sectionChange = changes.changedSections.find(c => c.sectionId === sectionId);

					// Find the section title from the original doc
					const originalSection = doc?.fileContent?.sections?.find((s) => s.id === sectionId);

					const subsections = extracted.subsections.map((sub) => {
						const originalSubsection = originalSection?.subsections?.find((ss) => ss.id === sub.id);

						// Check if this subsection changed
						const subChanged = sectionChange?.changedSubsections.includes(sub.id);

						return {
							id: sub.id,
							title: originalSubsection?.title || `Subsection ${sub.id}`,
							content: {
								html: subChanged ? sub.content : (originalSubsection?.content?.html || sub.content)
							},
							pageRange: {
								start: null,
								end: null
							}
						};
					});

					sections.push({
						id: sectionId,
						title: originalSection?.title || `Section ${sectionId}`,
						content: {
							html: sectionChange?.contentChanged ? extracted.content : (originalSection?.content?.html || extracted.content)
						},
						subsections: subsections,
						pageRange: {
							start: null,
							end: null
						}
					});
				});

				// Sort sections by their number
				sections.sort((a, b) => {
					const aNum = parseInt(a.id.split('-')[1]);
					const bNum = parseInt(b.id.split('-')[1]);
					return aNum - bNum;
				});

				return {
					...doc,
					fileContent: {
						...doc.fileContent,
						sections: sections
					},
					_changes: changes // Include change metadata
				};
			}
		} catch (error) {
			console.error('Error building updated document data:', error);
			return null;
		}
	};

	const function1 = async () => {
		const updatedData = buildUpdatedDocumentData();

		// Skip if no changes detected (reconciliation optimization)
		if (!updatedData) {
			return; // No changes, nothing to save
		}

		if (!updatedData.fileContent) {
			console.error('Failed to build updated document data');
			return;
		}

		// Log what changed for debugging
		if (updatedData._changes) {
			const changedCount = updatedData._changes.changedSections.length;
			const subsectionCount = updatedData._changes.changedSections.reduce(
				(sum, s) => sum + s.changedSubsections.length, 0
			);
			console.log(`💾 Saving changes: ${changedCount} section(s), ${subsectionCount} subsection(s)`);
		}

		try {
			await DocumentService.updateDocumentLocalFileContent(documentId, updatedData.fileContent);

			// CRITICAL: Update doc state with the new fileContent so subsequent saves build on top of this
			// Without this, the next save would use stale doc state and overwrite previous changes
			setDoc(prevDoc => {
				const nextDoc = {
					...prevDoc,
					fileContent: updatedData.fileContent
				};
				setSidebarSections(
					buildSidebarSectionsFromFileContent(updatedData.fileContent, nextDoc.title)
				);
				return nextDoc;
			});

			console.log('✅ Document updated successfully');
		} catch (error) {
			console.error('❌ Failed to update local document:', error);
		}
	};

	const function2 = async () => {
		if (pauseAutosyncRef.current) {
			console.log('Skipping server sync — metadata update in progress');
			return;
		}
		console.log('Function 2 executed');
		// Push latest fileContent from IndexedDB to server
		setSavingStatus('saving');
		const run = (async () => {
			try {
				await DocumentService.syncDocumentFileContent(documentId);
				console.log('Document synced to server successfully');
				if (!pauseAutosyncRef.current) {
					setSavingStatus('saved');
				}
			} catch (error) {
				console.error('Failed to sync document to server:', error);
				if (!pauseAutosyncRef.current) {
					setSavingStatus('error');
				}
				throw error;
			}
		})();

		syncInFlightRef.current = run.finally(() => {
			if (syncInFlightRef.current === run) {
				syncInFlightRef.current = null;
			}
		});
		return syncInFlightRef.current;
	};

	// Separate function/state for Manual Save button as requested
	const handleManualSave = async () => {
		console.log('Manual Save Executed');
		setManualSavingStatus('saving');
		// Also update the auto-save indicator to show activity, if desired, or keep separate.
		// User requested separation, but logically "saving" is true for both.
		// We'll keep the auto-save indicator independent or updated to 'saved' on success.

		try {
			await DocumentService.syncDocumentFileContent(documentId);
			console.log('Document manually synced to server successfully');

			// Handle Batch Image Deletion
			if (deletedImageUrls.current.size > 0) {
				const urlsToDelete = Array.from(deletedImageUrls.current);
				console.log('🗑️ Processing batch image deletion for:', urlsToDelete);
				try {
					await DocumentService.deleteDocumentImages(documentId, urlsToDelete);
					console.log('✅ Batch image deletion successful');
					deletedImageUrls.current.clear();
				} catch (imgError) {
					console.error('❌ Failed to delete images:', imgError);
				}
			}

			// Clear undo history to make this the new "starting point" as requested
			if (editorRef.current) {
				editorRef.current.undoManager.clear();
				console.log('Undo history cleared after manual save');
			}

			setManualSavingStatus('saved');
			setSavingStatus('saved'); // Sync the top indicator too

			// Optional: Reset manual status to idle after a delay
			setTimeout(() => {
				setManualSavingStatus('idle');
			}, 2000);
		} catch (error) {
			console.error('Failed to manually sync document:', error);
			setManualSavingStatus('error');
		}
	};

	// Direct save function for structural changes (reordering, etc.)
	// Bypasses reconciliation since we're saving the entire doc state
	// For move up/down: syncs to server IMMEDIATELY after IndexedDB save
	const saveDocumentStructure = async (newFileContent) => {
		if (!newFileContent) {
			console.error('No fileContent to save');
			return;
		}

		try {
			console.log('📂 Saving document structure...');
			await DocumentService.updateDocumentLocalFileContent(documentId, newFileContent);
			console.log('✅ Document structure saved to IndexedDB');

			// For structural changes (move up/down), sync to server immediately
			// Clear any pending timer since we're syncing now
			if (fn2TimerRef.current) {
				clearTimeout(fn2TimerRef.current);
				fn2TimerRef.current = null;
			}

			console.log('🔄 Syncing to server immediately...');
			await function2();

		} catch (error) {
			console.error('❌ Failed to save document structure:', error);
			throw error;
		}
	};

	// Track if function2 has been scheduled
	const fn2TimerRef = useRef(null);
	const syncInFlightRef = useRef(null);
	const pauseAutosyncRef = useRef(false);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (fn2TimerRef.current) {
				clearTimeout(fn2TimerRef.current);
			}
		};
	}, []);

	const debouncedFn1 = useDebounce(() => {
		// Execute function1 (save to IndexedDB)
		function1().catch(err => console.error('Error in function1:', err));

		// Schedule function2 ONLY if not already scheduled
		// This ensures function2 runs 30 seconds after the FIRST function1 call
		// Subsequent function1 calls won't reset/cancel the timer
		if (!fn2TimerRef.current) {
			console.log('⏱️ Scheduling server sync in 30 seconds...');
			fn2TimerRef.current = setTimeout(() => {
				function2().catch(err => console.error('Error in function2:', err));
				fn2TimerRef.current = null; // Reset after execution so new timer can be scheduled
			}, 30000);
		}
	}, 3000);

	const handleApaComplianceToggle = async (checked) => {
		if (!documentId) return;
		try {
			const prev =
				doc?.finalFormatSettings && typeof doc.finalFormatSettings === 'object'
					? doc.finalFormatSettings
					: {};
			const merged = { ...prev, includeApaComplianceStatement: checked };
			const res = await DocumentService.updateDocument(documentId, { finalFormatSettings: merged });
			const updated = res?.document;
			if (updated) {
				setDoc(updated);
				docRef.current = updated;
			} else {
				setDoc((d) => (d ? { ...d, finalFormatSettings: merged } : d));
				if (docRef.current) {
					docRef.current = { ...docRef.current, finalFormatSettings: merged };
				}
			}
			const editor = editorRef.current;
			if (editor) {
				const body = editor.getBody();
				const el = body.querySelector('#wf-apa-compliance-statement');
				if (checked && !el) {
					body.insertAdjacentHTML('beforeend', buildApaComplianceParagraphHtml(documentType));
				} else if (!checked && el) {
					el.remove();
				}
			}
			if (isInitialLoadComplete.current) {
				debouncedFn1();
			}
		} catch (e) {
			console.error(e);
			Toast.error(e?.response?.data?.message || 'Could not update settings');
		}
	};

	const handleOpenApaComplianceGuide = () => {
		const guideBody = getApaComplianceSectionGuideHtml().trim();
		const split = splitDissertationGuideHtml(guideBody);
		setSectionPreviewTitle('Section guide: APA compliance statement');
		setSectionPreviewSubtitle('Read-only. This does not change your document.');
		if (split.exampleHtml) {
			setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + split.guidanceHtml);
			setSectionGuideExampleHtml(SECTION_GUIDE_INTRO_HTML + split.exampleHtml);
		} else {
			setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + guideBody);
			setSectionGuideExampleHtml(null);
		}
		setSectionPreviewVariant('guide');
		setSectionPreviewOpen(true);
	};

	const handleEditorChange = (newContent, editor) => {
		// Guidance-only guard: references are auto-generated and manual edits are not durable.
		try {
			const getReferenceSectionIds = (fileContent) => {
				const ids = new Set();
				if (!fileContent?.categories) return ids;
				for (const category of fileContent.categories) {
					for (const section of category.sections || []) {
						const title = (section?.title || '').trim().toLowerCase();
						if (title === 'references' || title === 'reference list' || title === 'bibliography' || title === 'works cited') {
							if (section.id) ids.add(section.id);
						}
						for (const sub of section.subsections || []) {
							const st = (sub?.title || '').trim().toLowerCase();
							if (st === 'references' || st === 'reference list' || st === 'bibliography' || st === 'works cited') {
								if (sub.id) ids.add(sub.id);
							}
						}
					}
				}
				return ids;
			};
			const referenceIds = getReferenceSectionIds(docRef.current?.fileContent);
			if (referenceIds.size > 0 && editor?.selection) {
				let node = editor.selection.getNode();
				while (node && node.nodeType === 1 && node !== editor.getBody()) {
					if (node.id && referenceIds.has(node.id)) {
						const now = Date.now();
						if (now - lastReferencesEditWarningAtRef.current > 8000) {
							Toast.info('References are auto-generated from Source Library and may overwrite direct edits.');
							lastReferencesEditWarningAtRef.current = now;
						}
						break;
					}
					node = node.parentElement;
				}
			}
		} catch (e) {
			console.warn('References edit warning check failed:', e);
		}

		// Image Syncing / Deletion Logic
		try {
			// 1. Extract all current image sources from the editor content
			const doc = new DOMParser().parseFromString(newContent, 'text/html');
			const imgs = doc.querySelectorAll('img');
			const newImageSet = new Set();

			imgs.forEach(img => {
				const src = img.getAttribute('src');
				if (src) newImageSet.add(src);
			});

			// 2. Identify images that were present before but are missing now
			// We only run this check if we have an initial set of images (after first load)
			if (isInitialLoadComplete.current) {
				// 2a. Check for deletions (Old URL not in New Set)
				currentImageUrls.current.forEach(oldUrl => {
					// Ignore temporary blob/data URLs (e.g. during upload)
					if (oldUrl.startsWith('blob:') || oldUrl.startsWith('data:')) {
						return;
					}

					if (!newImageSet.has(oldUrl)) {
						// Image was deleted
						if (!deletedImageUrls.current.has(oldUrl)) {
							deletedImageUrls.current.add(oldUrl);
							console.log('🗑️ Image deleted:', oldUrl);
							console.log('📋 Current deleted images list:', Array.from(deletedImageUrls.current));
						}
					}
				});

				// 2b. Check for restorations/undo (New Set has URL that was in Deleted List)
				newImageSet.forEach(newUrl => {
					if (deletedImageUrls.current.has(newUrl)) {
						// Image was restored (undo/pasted back)
						deletedImageUrls.current.delete(newUrl);
						console.log('♻️ Image restored:', newUrl);
						console.log('📋 Current deleted images list:', Array.from(deletedImageUrls.current));
					}
				});

				// 3. Update current set for next comparison
				currentImageUrls.current = newImageSet;
			} else {
				// Initial population
				currentImageUrls.current = newImageSet;
			}

		} catch (e) {
			console.error('Error tracking image deletions:', e);
		}

		// Don't update state on every change (uncontrolled component pattern)
		// Only call debounced save functions after initial content is loaded
		if (isInitialLoadComplete.current) {
			debouncedFn1();
		}
	};

	const syncOutlineHighlightFromEditor = (editor, preferScroll = false) => {
		if (!editor) return;
		const body = editor.getBody();
		if (!body) return;

		const sections = sidebarSectionsRef.current;
		let nextId = null;

		if (preferScroll) {
			nextId = resolveActiveOutlineIdFromScroll(editor, sections);
		} else {
			const node = editor.selection?.getNode();
			nextId = resolveActiveOutlineIdFromElement(node, body, sections);
			if (!nextId) {
				nextId = resolveActiveOutlineIdFromScroll(editor, sections);
			}
		}

		if (nextId) {
			setCursorSectionId((prev) => (prev === nextId ? prev : nextId));
		}
	};

	const handleSidebarNavigate = (id) => {
		try {
			const editor = editorRef.current;
			if (!editor) return;

			const editorBody = editor.getBody();
			const sectionTitle = (() => {
				for (const cat of sidebarSectionsRef.current || []) {
					for (const sec of cat.sections || []) {
						if (sec.id === id) return sec.title || '';
						for (const sub of sec.subsections || []) {
							if (sub.id === id) return sec.title || '';
						}
						const virtual = parseVirtualH2NavId(id);
						if (virtual && sec.id === virtual.sectionId) return sec.title || '';
					}
				}
				return '';
			})();

			const scrollTarget =
				resolveDomElementForNavId(editorBody, id, sectionTitle) ||
				editorBody.querySelector(`#${CSS.escape(id)}`);

			if (!scrollTarget) {
				console.warn('Target element not found:', id);
				return;
			}

			scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
			const focusNode =
				scrollTarget.matches?.('h1,h2,h3,h4,h5,h6,p,ul,ol,blockquote,pre,table')
					? scrollTarget
					: scrollTarget.querySelector('h1, h2, h3, h4, h5, h6, p, ul, ol, blockquote, pre, table') ||
						scrollTarget.firstElementChild;

			if (focusNode) {
				editor.selection.setCursorLocation(focusNode, 0);
			}
			editor.focus();
			setCursorSectionId(id);
		} catch (err) {
			console.error('Navigation error:', err);
		}
	};

	// Move section up/down within its category, or into an adjacent category at boundaries
	const handleMoveSection = async (categoryId, sectionId, direction) => {
		if (!doc?.fileContent?.categories) return;

		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const newCategories = doc.fileContent.categories.map((c) => ({
			...c,
			sections: [...(c.sections || [])]
		}));

		const category = newCategories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
		let newFileContent = null;

		if (newIndex >= 0 && newIndex < category.sections.length) {
			const newSections = [...category.sections];
			[newSections[sectionIndex], newSections[newIndex]] = [newSections[newIndex], newSections[sectionIndex]];
			newCategories[categoryIndex] = { ...category, sections: newSections };
			newFileContent = { ...doc.fileContent, categories: newCategories };
		} else if (direction === 'down' && newIndex >= category.sections.length && categoryIndex < newCategories.length - 1) {
			const nextIdx = categoryIndex + 1;
			const [moved] = category.sections.splice(sectionIndex, 1);
			newCategories[categoryIndex] = { ...category, sections: category.sections };
			const nextCat = newCategories[nextIdx];
			const nextSections = [...(nextCat.sections || [])];
			nextSections.unshift(moved);
			newCategories[nextIdx] = { ...nextCat, sections: nextSections };
			newFileContent = { ...doc.fileContent, categories: newCategories };
		} else if (direction === 'up' && newIndex < 0 && categoryIndex > 0) {
			const prevIdx = categoryIndex - 1;
			const [moved] = category.sections.splice(sectionIndex, 1);
			newCategories[categoryIndex] = { ...category, sections: category.sections };
			const prevCat = newCategories[prevIdx];
			const prevSections = [...(prevCat.sections || [])];
			prevSections.push(moved);
			newCategories[prevIdx] = { ...prevCat, sections: prevSections };
			newFileContent = { ...doc.fileContent, categories: newCategories };
		} else {
			Toast.info(
				`Cannot move section ${direction} — already at the ${direction === 'up' ? 'start' : 'end'} of the document. ` +
					'Some programs require Abstract in front matter; reorder only if your institution allows it.'
			);
			return;
		}

		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		setSidebarSections(
			buildSidebarSectionsFromFileContent(newFileContent, doc.title)
		);

		const editor = editorRef.current;
		if (editor) {
			syncEditorSectionOrderFromFileContent(editor, newFileContent);
		}

		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`Section moved ${direction}`);
		} catch (error) {
			Toast.error('Failed to save section order');
		}
	};

	// Move subsection up/down within its parent section
	const handleMoveSubsection = async (categoryId, sectionId, subsectionId, direction) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		const section = category.sections[sectionIndex];
		if (!section.subsections) return;

		const subsectionIndex = section.subsections.findIndex(sub => sub.id === subsectionId);
		if (subsectionIndex === -1) return;

		// Calculate new index
		const newIndex = direction === 'up' ? subsectionIndex - 1 : subsectionIndex + 1;

		// Check bounds
		if (newIndex < 0 || newIndex >= section.subsections.length) {
			Toast.info(`Cannot move subsection ${direction} - already at the ${direction === 'up' ? 'top' : 'bottom'}`);
			return;
		}

		// Swap subsections in the array
		const newSubsections = [...section.subsections];
		[newSubsections[subsectionIndex], newSubsections[newIndex]] = [newSubsections[newIndex], newSubsections[subsectionIndex]];

		// Build the new fileContent
		const newSections = [...category.sections];
		newSections[sectionIndex] = { ...section, subsections: newSubsections };

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];
			if (sidebarCategory?.sections) {
				const sidebarSection = sidebarCategory.sections[sectionIndex];
				if (sidebarSection?.subsections) {
					const newSidebarSubsections = [...sidebarSection.subsections];
					[newSidebarSubsections[subsectionIndex], newSidebarSubsections[newIndex]] =
						[newSidebarSubsections[newIndex], newSidebarSubsections[subsectionIndex]];

					const newSidebarCategorySections = [...sidebarCategory.sections];
					newSidebarCategorySections[sectionIndex] = { ...sidebarSection, subsections: newSidebarSubsections };
					newSidebarSections[categoryIndex] = { ...sidebarCategory, sections: newSidebarCategorySections };
				}
			}
			return newSidebarSections;
		});

		// Update editor content - swap the subsection divs within the parent section
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const parentSection = editorBody.querySelector(`#${CSS.escape(sectionId)}`);
			if (parentSection) {
				const currentSubsection = parentSection.querySelector(`#${CSS.escape(subsectionId)}`);
				const swapSubsection = parentSection.querySelector(`#${CSS.escape(newSubsections[subsectionIndex].id)}`);

				if (currentSubsection && swapSubsection) {
					if (direction === 'up') {
						swapSubsection.parentNode.insertBefore(currentSubsection, swapSubsection);
					} else {
						swapSubsection.parentNode.insertBefore(currentSubsection, swapSubsection.nextSibling);
					}
				}
			}
		}

		// Save directly (bypass reconciliation since this is a structural change)
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`Subsection moved ${direction}`);
		} catch (error) {
			Toast.error('Failed to save subsection order');
		}
	};

	// Add optional section to the document (creates a copy from template)
	const handleAddSection = async (categoryId, sectionId) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category and template section
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const templateSectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (templateSectionIndex === -1) return;

		const templateSection = category.sections[templateSectionIndex];


		// Check if we are restoring a REQUIRED section (visible: false -> true)
		if (templateSection.required === true && templateSection.isVisible === false) {

			// 1. Restore visibility and set content to editor scaffold (not section guide)
			const restoredSection = {
				...templateSection,
				isVisible: true,
				content: {
					html: getSectionRestoreHtml(templateSection, {
						categoryName: category.name,
						documentTypeName: documentType
					})
				},
				// Keep subsections as they are (preserving isVisible: false if they were deleted)
				subsections: (templateSection.subsections || [])
			};

			// Build new sections array - just replace the hidden one
			const newSections = [...category.sections];
			newSections[templateSectionIndex] = restoredSection;

			const newCategories = [...doc.fileContent.categories];
			newCategories[categoryIndex] = { ...category, sections: newSections };
			const newFileContent = { ...doc.fileContent, categories: newCategories };

			// Update doc state
			setDoc(prevDoc => ({
				...prevDoc,
				fileContent: newFileContent
			}));

			// Update sidebar state
			setSidebarSections(prevSections => {
				const newSidebarSections = [...prevSections];
				const sidebarCategory = newSidebarSections[categoryIndex];

				if (sidebarCategory) {
					// Add back to included sections
					const newIncludedSections = newSections
						.filter(s => s.isVisible !== false)
						.map(s => ({
							id: s.id,
							title: s.title,
							canDelete: (s.required === false && s.id.includes('-added-')) || s.required === true,

							// --- FIX: Filter subsections correctly for the sidebar ---

							// 1. Only show VISIBLE subsections in the active list
							subsections: (s.subsections || [])
								.filter((sub) => sub.isVisible !== false && (sub.optional === false || sub.optional === undefined))
								.map((sub) => ({
									id: sub.id,
									title: sub.title,
									canDelete: (sub.required === false && sub.id.includes('-added-')) || sub.required === true
								})),

							// 2. Put hidden optional subsections here
							optionalSubsections: (s.subsections || [])
								.filter((sub) => sub.required === false && sub.optional === true && sub.isVisible !== false)
								.map((sub) => ({
									id: sub.id,
									title: sub.title
								})),

							// 3. Put hidden REQUIRED subsections here (so they can be restored)
							deletedRequiredSubsections: (s.subsections || [])
								.filter((sub) => sub.required === true && sub.isVisible === false)
								.map((sub) => ({
									id: sub.id,
									title: sub.title
								}))
						}));

					// Remove from deletedRequiredSections list
					const newDeletedRequiredSections = (sidebarCategory.deletedRequiredSections || [])
						.filter(s => s.id !== sectionId);

					newSidebarSections[categoryIndex] = {
						...sidebarCategory,
						sections: newIncludedSections,
						deletedRequiredSections: newDeletedRequiredSections
					};
				}
				return newSidebarSections;
			});

			// Build HTML and Insert into Editor
			const editor = editorRef.current;
			if (editor) {
				const editorBody = editor.getBody();

				// Build HTML
				const restoredBreakClass = shouldForceSectionPageBreak ? 'page-break-before' : '';
				let sectionHtml = `<div id="${restoredSection.id}" class="${restoredBreakClass}">
					${restoredSection.content?.html}
				`;

				// FIX: Only render visible subsections into the HTML
				if (restoredSection.subsections && Array.isArray(restoredSection.subsections)) {
					restoredSection.subsections.forEach((sub) => {
						if (sub.isVisible !== false && (sub.optional === false || sub.optional === undefined)) {
							sectionHtml += `
								<div id="${sub.id}">
									${sub.content?.html}
								</div>
							`;
						}
					});
				}
				sectionHtml += `</div>`;

				// Create Element
				const tempDiv = document.createElement('div');
				tempDiv.innerHTML = sectionHtml;
				const newSectionElement = tempDiv.firstChild;

				// Determine Insertion Point
				// We need to find the correct visual position among currently visible sections
				const visibleSections = [];
				newCategories.forEach(cat => {
					if (cat.sections) {
						cat.sections.forEach(sec => {
							if (sec.isVisible !== false) {
								visibleSections.push(sec.id);
							}
						});
					}
				});

				const newSectionIndex = visibleSections.indexOf(sectionId);
				let inserted = false;

				if (newSectionIndex > 0) {
					const prevSectionId = visibleSections[newSectionIndex - 1];
					const prevElement = editorBody.querySelector(`#${CSS.escape(prevSectionId)}`);
					if (prevElement) {
						prevElement.parentNode.insertBefore(newSectionElement, prevElement.nextSibling);
						inserted = true;
					}
				} else if (newSectionIndex === 0) {
					if (editorBody.firstChild) {
						editorBody.insertBefore(newSectionElement, editorBody.firstChild);
						inserted = true;
					}
				}

				if (!inserted) {
					editorBody.appendChild(newSectionElement);
				}
			}

			initializePreviousContentFromEditor();
			try {
				await saveDocumentStructure(newFileContent);
				Toast.success(`"${restoredSection.title}" restored`);
			} catch (error) {
				Toast.error('Failed to restore section');
			}

			return; // EXIT early for required logic
		}

		// --- OPTIONAL SECTION LOGIC (Original "Add" Logic) ---

		const planFeatures = getPlanFeatures(getEffectivePlanName());
		if (!planFeatures?.canUseTemplates) {
			Toast.error('Upgrade to Essayist or Laureate to add custom sections.');
			return;
		}

		// Check if section is actually a template (required: false, optional: true, isVisible: true)
		if (templateSection.required !== false || templateSection.optional !== true) {
			Toast.info('This section is already part of the document');
			return;
		}

		// Generate a unique ID for the new section copy
		const newSectionId = `${sectionId}-added-${Date.now()}`;

		// Create a duplicate section with new properties
		const duplicatedSection = {
			...templateSection,
			id: newSectionId,
			required: false,
			optional: false,
			isVisible: true,
			// Deep clone subsections with new IDs
			subsections: (templateSection.subsections || []).map((sub, idx) => ({
				...sub,
				id: `${sub.id}-added-${Date.now()}-${idx}`
			}))
		};

		// Hide the template section (set isVisible: false)
		const hiddenTemplateSection = {
			...templateSection,
			isVisible: false
		};

		// Build new sections array: update template + add duplicate after it
		const newSections = [...category.sections];
		newSections[templateSectionIndex] = hiddenTemplateSection;
		// Insert the duplicated section right after the template
		newSections.splice(templateSectionIndex + 1, 0, duplicatedSection);

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];

			if (sidebarCategory) {
				// Create new section object for sidebar (with delete capability)
				const newSidebarSection = {
					id: newSectionId,
					title: duplicatedSection.title,
					canDelete: true, // Added sections can be deleted
					subsections: (duplicatedSection.subsections || [])
						.filter((sub) => sub.isVisible !== false && (sub.optional === false || sub.optional === undefined))
						.map((sub) => ({
							id: sub.id,
							title: sub.title,
							canDelete: true // All subsections in an optional section are clones, so effectively deletable if needed
						})),
					optionalSubsections: (duplicatedSection.subsections || [])
						.filter((sub) => sub.required === false && sub.optional === true && sub.isVisible !== false)
						.map((sub) => ({ id: sub.id, title: sub.title })),
					deletedRequiredSubsections: []
				};

				// Append at the end of sections (added sections go at the end)
				const newIncludedSections = [
					...(sidebarCategory.sections || []),
					newSidebarSection
				];

				// Remove from optional sections (template is now hidden)
				const newOptionalSections = (sidebarCategory.optionalSections || [])
					.filter(s => s.id !== sectionId);

				newSidebarSections[categoryIndex] = {
					...sidebarCategory,
					sections: newIncludedSections,
					optionalSections: newOptionalSections
				};
			}

			return newSidebarSections;
		});

		// Build HTML for the new section and insert in editor
		const editor = editorRef.current;
		if (editor) {
			const newSectionBreakClass = shouldForceSectionPageBreak ? 'page-break-before' : '';
			let sectionHtml = `<div id="${newSectionId}" class="${newSectionBreakClass}">
				${duplicatedSection.content?.html || '<p><br></p>'}
			`;

			// Include subsections
			if (duplicatedSection.subsections && Array.isArray(duplicatedSection.subsections)) {
				duplicatedSection.subsections.forEach((sub) => {
					if (sub.optional === false || sub.optional === undefined) {
						sectionHtml += `
							<div id="${sub.id}">
								${sub.content?.html || '<p><br></p>'}
							</div>
						`;
					}
				});
			}

			sectionHtml += `</div>`;

			const editorBody = editor.getBody();
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = sectionHtml;
			const newSectionElement = tempDiv.firstChild;

			// Determine correct insertion index
			const visibleSections = [];
			newCategories.forEach(cat => {
				if (cat.sections) {
					cat.sections.forEach(sec => {
						if (sec.isVisible !== false) {
							visibleSections.push(sec.id);
						}
					});
				}
			});

			const newSectionIndex = visibleSections.indexOf(newSectionId);
			let inserted = false;

			if (newSectionIndex > 0) {
				const prevSectionId = visibleSections[newSectionIndex - 1];
				const prevElement = editorBody.querySelector(`#${CSS.escape(prevSectionId)}`);
				if (prevElement) {
					prevElement.parentNode.insertBefore(newSectionElement, prevElement.nextSibling);
					inserted = true;
				}
			} else if (newSectionIndex === 0) {
				if (editorBody.firstChild) {
					editorBody.insertBefore(newSectionElement, editorBody.firstChild);
					inserted = true;
				}
			}

			if (!inserted) {
				// Fallback: Append at the end of the editor content
				editorBody.appendChild(newSectionElement);
			}
		}

		// Reinitialize the previous content ref for change detection
		initializePreviousContentFromEditor();

		// Save directly to IndexedDB and sync to server
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`"${duplicatedSection.title}" added to document`);
		} catch (error) {
			Toast.error('Failed to add section');
		}
	};

	// Delete an added section (restore the template)
	const handleDeleteSection = async (categoryId, sectionId) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category and the added section
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		const sectionToDelete = category.sections[sectionIndex];

		const titleLower = (sectionToDelete.title || '').toLowerCase();
		const isProtectedTemplate =
			sectionToDelete.required === true &&
			(titleLower.includes('copyright') ||
				titleLower.includes('approval') ||
				(titleLower.includes('signature') && titleLower.includes('page')));
		if (isProtectedTemplate) {
			Toast.error(
				'Copyright and Approval/Signature pages are fixed by your template. Edit the text inside the section instead of removing it.'
			);
			return;
		}

		// Check if this section can be deleted
		// Added sections have ID pattern: originalId-added-timestamp
		const isAddedSection = sectionId.includes('-added-');

		// ALLOW deleting required sections now (different logic: soft delete/hide)
		if (sectionToDelete.required !== true && !isAddedSection) {
			Toast.error('This section cannot be deleted');
			return;
		}

		// === HANDLING REQUIRED SECTIONS (Soft Delete) ===
		if (sectionToDelete.required === true) {
			// 1. Hide the section (isVisible: false)
			// 2. Reset content to editor scaffold (so when restored it's fresh)

			const hiddenSection = {
				...sectionToDelete,
				isVisible: false,
				content: {
					html: getSectionRestoreHtml(sectionToDelete, {
						categoryName: category.name,
						documentTypeName: documentType
					})
				},
				subsections: (sectionToDelete.subsections || []).map(sub => ({
					...sub,
					content: {
						html: getSectionRestoreHtml(sub, {
							categoryName: category.name,
							parentTitle: sectionToDelete.title,
							documentTypeName: documentType
						})
					}
				}))
			};

			// Build new sections array: Replace current with hidden
			const newSections = [...category.sections];
			newSections[sectionIndex] = hiddenSection;

			const newCategories = [...doc.fileContent.categories];
			newCategories[categoryIndex] = { ...category, sections: newSections };
			const newFileContent = { ...doc.fileContent, categories: newCategories };

			// Update Doc State
			setDoc(prevDoc => ({
				...prevDoc,
				fileContent: newFileContent
			}));

			// Update Sidebar State
			setSidebarSections(prevSections => {
				const newSidebarSections = [...prevSections];
				const sidebarCategory = newSidebarSections[categoryIndex];

				if (sidebarCategory) {
					// Remove from Included Sections
					const newIncludedSections = (sidebarCategory.sections || [])
						.filter(s => s.id !== sectionId);

					// Add to Deleted Required Sections (Restorable List)
					const newDeletedRequiredSections = [...(sidebarCategory.deletedRequiredSections || [])];
					newDeletedRequiredSections.push({
						id: sectionId,
						title: sectionToDelete.title
					});

					newSidebarSections[categoryIndex] = {
						...sidebarCategory,
						sections: newIncludedSections,
						deletedRequiredSections: newDeletedRequiredSections
					};
				}
				return newSidebarSections;
			});

			// Remove from Editor DOM
			const editor = editorRef.current;
			if (editor) {
				const editorBody = editor.getBody();
				const sectionElement = editorBody.querySelector(`#${sectionId}`);
				if (sectionElement) {
					sectionElement.remove();
				}
			}

			initializePreviousContentFromEditor();
			try {
				await saveDocumentStructure(newFileContent);
				Toast.success(`"${sectionToDelete.title}" removed`);
			} catch (error) {
				Toast.error('Failed to remove section');
			}
			return;
		}


		// === HANDLING OPTIONAL ADDED SECTIONS (Permanent Delete) ===

		// Extract template section ID from the added section's ID pattern
		const templateSectionId = sectionId.split('-added-')[0];

		// Find the template section and restore its visibility
		const templateSectionIndex = category.sections.findIndex(s => s.id === templateSectionId);

		// Build new sections array
		const newSections = category.sections.filter(s => s.id !== sectionId);

		// Restore template visibility if found
		if (templateSectionIndex !== -1) {
			const templateSection = category.sections[templateSectionIndex];
			const restoredTemplate = {
				...templateSection,
				isVisible: true
			};
			// Find the index in newSections (it shifted after removing the deleted section)
			const newTemplateIndex = newSections.findIndex(s => s.id === templateSectionId);
			if (newTemplateIndex !== -1) {
				newSections[newTemplateIndex] = restoredTemplate;
			}
		}

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];

			if (sidebarCategory) {
				// Remove the deleted section from sidebar
				const newIncludedSections = (sidebarCategory.sections || [])
					.filter(s => s.id !== sectionId);

				// Restore template to optional sections if it exists
				const templateSection = category.sections.find(s => s.id === templateSectionId);
				let newOptionalSections = [...(sidebarCategory.optionalSections || [])];

				if (templateSection) {
					newOptionalSections.push({
						id: templateSectionId,
						title: templateSection.title
					});
				}

				newSidebarSections[categoryIndex] = {
					...sidebarCategory,
					sections: newIncludedSections,
					optionalSections: newOptionalSections
				};
			}

			return newSidebarSections;
		});

		// Remove the section from the editor
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const sectionElement = editorBody.querySelector(`#${sectionId}`);
			if (sectionElement) {
				sectionElement.remove();
			}
		}

		// Reinitialize the previous content ref for change detection
		initializePreviousContentFromEditor();

		// Save directly to IndexedDB and sync to server
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`"${sectionToDelete.title}" removed from document`);
		} catch (error) {
			Toast.error('Failed to remove section');
		}
	};

	//handle add subsection
	const handleAddSubsection = async (categoryId, sectionId, subsectionId) => {
		if (!doc?.fileContent?.categories) return;

		// 1. Find indices for Doc State
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;
		const category = doc.fileContent.categories[categoryIndex];

		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;
		const section = category.sections[sectionIndex];

		const templateSubIndex = section.subsections.findIndex(s => s.id === subsectionId);
		if (templateSubIndex === -1) return;
		const templateSub = section.subsections[templateSubIndex];

		// Helper for sample content
		const getSampleContent = (item) => {
			if (item.sampleContent && typeof item.sampleContent === 'object' && item.sampleContent.html) {
				return item.sampleContent.html;
			} else if (typeof item.sampleContent === 'string') {
				return item.sampleContent;
			}
			return '<p><br></p>';
		};

		let newSubId = '';
		let newSubTitle = '';
		let isRestore = false;

		// Declare updated data structures in outer scope
		let newCategories = null;
		let newSections = null;

		// --- LOGIC A: Restore Required Subsection ---
		if (templateSub.required === true && templateSub.isVisible === false) {
			isRestore = true;
			newSubId = templateSub.id;
			newSubTitle = templateSub.title;

			const restoredSub = {
				...templateSub,
				isVisible: true,
				content: { html: getSampleContent(templateSub) }
			};

			const newSubsections = [...section.subsections];
			newSubsections[templateSubIndex] = restoredSub;

			// Update Doc State logic
			newSections = [...category.sections];
			newSections[sectionIndex] = { ...section, subsections: newSubsections };

			newCategories = [...doc.fileContent.categories];
			newCategories[categoryIndex] = { ...category, sections: newSections };

			const newFileContent = { ...doc.fileContent, categories: newCategories };
			setDoc(prev => ({ ...prev, fileContent: newFileContent }));
		}
		// --- LOGIC B: Add Optional Subsection ---
		else if (templateSub.required === false && templateSub.optional === true) {
			isRestore = false;
			newSubId = `${subsectionId}-added-${Date.now()}`;
			newSubTitle = templateSub.title;

			const duplicatedSub = {
				...templateSub,
				id: newSubId,
				required: false,
				optional: false,
				isVisible: true,
				content: { html: getSampleContent(templateSub) }
			};

			const hiddenTemplate = { ...templateSub, isVisible: false };

			const newSubsections = [...section.subsections];
			newSubsections[templateSubIndex] = hiddenTemplate;
			newSubsections.splice(templateSubIndex + 1, 0, duplicatedSub);

			// Update Doc State logic
			newSections = [...category.sections];
			newSections[sectionIndex] = { ...section, subsections: newSubsections };

			newCategories = [...doc.fileContent.categories];
			newCategories[categoryIndex] = { ...category, sections: newSections };

			const newFileContent = { ...doc.fileContent, categories: newCategories };
			setDoc(prev => ({ ...prev, fileContent: newFileContent }));
		} else {
			return; // Invalid case
		}

		// --- UPDATE SIDEBAR STATE (Correct Positioning Logic) ---
		setSidebarSections(prevSections => {
			return prevSections.map(cat => {
				if (cat.id !== categoryId) return cat;

				const updatedSidebarSections = cat.sections.map(sec => {
					if (sec.id !== sectionId) return sec;

					// 1. Get the UPDATED source list from our calculations above
					// This array (newSections) already has the correct order and visible flags
					const updatedDocSection = newSections.find(s => s.id === sectionId);

					// 2. Re-generate the Active Subsections list from scratch using the updated source
					// This ensures the order exactly matches the document structure
					const newActiveSubsections = (updatedDocSection.subsections || [])
						.filter(sub => sub.isVisible !== false && (sub.optional === false || sub.optional === undefined))
						.map(sub => ({
							id: sub.id,
							title: sub.title,
							canDelete: (sub.required === false && sub.id.includes('-added-')) || sub.required === true
						}));

					// 3. Update the Optional/Deleted lists
					// We can reuse the previous state filters or re-calculate them. 
					// Re-calculating is safer to ensure sync with Doc State.
					const newOptionalSubsections = (updatedDocSection.subsections || [])
						.filter(sub => sub.required === false && sub.optional === true && sub.isVisible !== false)
						.map(sub => ({
							id: sub.id,
							title: sub.title
						}));

					const newDeletedRequiredSubsections = (updatedDocSection.subsections || [])
						.filter(sub => sub.required === true && sub.isVisible === false)
						.map(sub => ({
							id: sub.id,
							title: sub.title
						}));

					// 4. Return updated sidebar object
					return {
						...sec,
						subsections: newActiveSubsections,
						optionalSubsections: newOptionalSubsections,
						deletedRequiredSubsections: newDeletedRequiredSubsections
					};
				});

				return { ...cat, sections: updatedSidebarSections };
			});
		});

		// --- INSERT INTO EDITOR DOM ---
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const parentSection = editorBody.querySelector(`#${CSS.escape(sectionId)}`);
			if (parentSection) {
				const contentHtml = isRestore ? getSampleContent(templateSub) : getSampleContent(templateSub);
				const subHtml = `<div id="${newSubId}">${contentHtml}</div>`;
				const tempDiv = document.createElement('div');
				tempDiv.innerHTML = subHtml;
				const newNode = tempDiv.firstChild;

				if (!editorBody.querySelector(`#${CSS.escape(newSubId)}`)) {
					let inserted = false;

					const freshSubs = newSections[sectionIndex].subsections || [];
					const newIdx = freshSubs.findIndex(s => s.id === newSubId);

					if (newIdx !== -1) {
						for (let i = newIdx + 1; i < freshSubs.length; i++) {
							const potentialNeighbor = freshSubs[i];
							if (potentialNeighbor.isVisible === false) continue;
							if (potentialNeighbor.optional === true && potentialNeighbor.required === false) continue;
							const neighborEl = parentSection.querySelector(`#${CSS.escape(potentialNeighbor.id)}`);
							if (neighborEl) {
								parentSection.insertBefore(newNode, neighborEl);
								inserted = true;
								break;
							}
						}
					}

					if (!inserted) {
						parentSection.appendChild(newNode);
					}
				}
			}
		}

		initializePreviousContentFromEditor();

		setTimeout(() => {
			if (docRef.current?.fileContent && newCategories) {
				const contentToSave = { ...docRef.current.fileContent, categories: newCategories };
				saveDocumentStructure(contentToSave);
			}
		}, 50);

		Toast.success(`"${newSubTitle}" added`);
	};
	//handle subsection deletions 
	const handleDeleteSubsection = async (categoryId, sectionId, subsectionId) => {
		if (!doc?.fileContent?.categories) return;

		// --- 1. PREPARE DATA ---
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;
		const category = doc.fileContent.categories[categoryIndex];

		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;
		const section = category.sections[sectionIndex];

		const subIndex = section.subsections.findIndex(s => s.id === subsectionId);
		if (subIndex === -1) return;
		const subToDelete = section.subsections[subIndex];

		const isAddedSub = subsectionId.includes('-added-');

		if (subToDelete.required !== true && !isAddedSub) {
			Toast.error('Cannot delete this subsection');
			return;
		}

		let updatedDocSubsections = [...section.subsections];
		let isRequiredDelete = false;
		let templateIdToRestore = null;

		// --- 2. LOGIC A: Soft Delete Required ---
		if (subToDelete.required === true) {
			isRequiredDelete = true;
			const getSampleContent = (item) => {
				if (item.sampleContent && typeof item.sampleContent === 'object' && item.sampleContent.html) return item.sampleContent.html;
				return typeof item.sampleContent === 'string' ? item.sampleContent : '<p><br></p>';
			};

			const hiddenSub = {
				...subToDelete,
				isVisible: false,
				content: { html: getSampleContent(subToDelete) }
			};
			updatedDocSubsections[subIndex] = hiddenSub;
		}
		// --- 3. LOGIC B: Hard Delete Optional ---
		else {
			// Remove the added subsection
			updatedDocSubsections = updatedDocSubsections.filter(s => s.id !== subsectionId);

			// Restore template visibility
			templateIdToRestore = subsectionId.split('-added-')[0];
			const templateIndex = updatedDocSubsections.findIndex(s => s.id === templateIdToRestore);
			if (templateIndex !== -1) {
				updatedDocSubsections[templateIndex] = { ...updatedDocSubsections[templateIndex], isVisible: true };
			}
		}

		// --- 4. UPDATE DOC STATE ---
		const newSections = [...category.sections];
		newSections[sectionIndex] = { ...section, subsections: updatedDocSubsections };
		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		setDoc(prev => ({ ...prev, fileContent: newFileContent }));

		// --- 5. UPDATE SIDEBAR STATE (Immutable Pattern) ---
		setSidebarSections(prevSections => {
			return prevSections.map(cat => {
				if (cat.id !== categoryId) return cat;

				// Map sections
				const updatedSidebarSections = cat.sections.map(sec => {
					if (sec.id !== sectionId) return sec;

					// Filter out the deleted item from active list
					const newActiveSubsections = (sec.subsections || []).filter(s => s.id !== subsectionId);

					// Prepare restore lists
					let newOptionalSubsections = [...(sec.optionalSubsections || [])];
					let newDeletedRequiredSubsections = [...(sec.deletedRequiredSubsections || [])];

					if (isRequiredDelete) {
						// Add to Restore List
						newDeletedRequiredSubsections.push({
							id: subsectionId,
							title: subToDelete.title
						});
					} else {
						// Add to Add List (using template ID)
						// Ensure we don't add duplicates if logic runs twice
						if (!newOptionalSubsections.find(s => s.id === templateIdToRestore)) {
							newOptionalSubsections.push({
								id: templateIdToRestore,
								title: subToDelete.title
							});
						}
					}

					return {
						...sec,
						subsections: newActiveSubsections,
						optionalSubsections: newOptionalSubsections,
						deletedRequiredSubsections: newDeletedRequiredSubsections
					};
				});

				return { ...cat, sections: updatedSidebarSections };
			});
		});

		// --- 6. UPDATE EDITOR DOM ---
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const subElement = editorBody.querySelector(`#${CSS.escape(subsectionId)}`);
			if (subElement) subElement.remove();
		}

		initializePreviousContentFromEditor();

		setTimeout(() => {
			if (docRef.current?.fileContent) {
				const contentToSave = { ...docRef.current.fileContent, categories: newCategories };
				saveDocumentStructure(contentToSave);
			}
		}, 50);

		Toast.success('Subsection removed');
	};

	const resolveItemMeta = (itemId) => {
		const fc = docRef.current?.fileContent;
		if (!itemId || !fc?.categories) return { title: 'Section', categoryName: '', parentTitle: '' };
		for (const category of fc.categories) {
			if (!category.sections) continue;
			for (const section of category.sections) {
				if (section.id === itemId) {
					return {
						title: section.title || 'Section',
						categoryName: category.name || '',
						parentTitle: ''
					};
				}
				if (section.subsections) {
					const sub = section.subsections.find((s) => s.id === itemId);
					if (sub) {
						return {
							title: sub.title || 'Section',
							categoryName: category.name || '',
							parentTitle: section.title || ''
						};
					}
				}
			}
		}
		return { title: 'Section', categoryName: '', parentTitle: '' };
	};

	// --- Section Preview (current section HTML; custom modal — no mcePreview / no extra Close button) ---
	const handlePreviewSection = (sectionId) => {
		const editor = editorRef.current;
		if (!editor) return;

		const node = editor.dom.get(sectionId);
		if (!node) {
			Toast.error('Section content not found');
			return;
		}

		const itemMeta = resolveItemMeta(sectionId);
		setSectionPreviewTitle(`Preview: ${itemMeta.title}`);
		setSectionPreviewSubtitle('Your current document content.');
		setSectionPreviewHtml(node.outerHTML);
		setSectionGuideExampleHtml(null);
		setSectionPreviewVariant('preview');
		setSectionPreviewOpen(true);
	};

	// --- Section guide (read-only sample from fileContent) ---
	const handleSampleContent = (itemId) => {
		const itemMeta = resolveItemMeta(itemId);
		const itemTitle = itemMeta.title;
		const chapter1Key = matchDissertationChapter1Key(itemTitle, {
			categoryName: itemMeta.categoryName,
			parentTitle: itemMeta.parentTitle,
			documentTypeName: documentType
		});
		const canonicalGuide = chapter1Key ? getDissertationSectionGuideHtml(chapter1Key) : null;
		if (canonicalGuide) {
			const split = splitDissertationGuideHtml(canonicalGuide);
			setSectionPreviewTitle(`Section guide: ${itemTitle}`);
			setSectionPreviewSubtitle('Read-only. This does not change your document.');
			if (split.exampleHtml) {
				setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + split.guidanceHtml);
				setSectionGuideExampleHtml(SECTION_GUIDE_INTRO_HTML + split.exampleHtml);
			} else {
				setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + canonicalGuide);
				setSectionGuideExampleHtml(null);
			}
			setSectionPreviewVariant('guide');
			setSectionPreviewOpen(true);
			return;
		}

		let foundContent = '';
		let found = false;

		for (const section of getTopLevelSections(doc?.fileContent)) {
			if (section.id === itemId) {
				if (section.guideContent && typeof section.guideContent === 'object' && section.guideContent.html) {
					foundContent += section.guideContent.html;
				} else if (typeof section.guideContent === 'string') {
					foundContent += section.guideContent;
				} else if (section.sampleContent && typeof section.sampleContent === 'object' && section.sampleContent.html) {
					foundContent += section.sampleContent.html;
				} else if (typeof section.sampleContent === 'string') {
					foundContent += section.sampleContent;
				}

				if (section.subsections && Array.isArray(section.subsections)) {
					for (const sub of section.subsections) {
						if (sub.guideContent && typeof sub.guideContent === 'object' && sub.guideContent.html) {
							foundContent += sub.guideContent.html;
						} else if (typeof sub.guideContent === 'string') {
							foundContent += sub.guideContent;
						} else if (sub.sampleContent && typeof sub.sampleContent === 'object' && sub.sampleContent.html) {
							foundContent += sub.sampleContent.html;
						} else if (typeof sub.sampleContent === 'string') {
							foundContent += sub.sampleContent;
						}
					}
				}
				found = true;
				break;
			}

			if (section.subsections) {
				const subsection = section.subsections.find((sub) => sub.id === itemId);
				if (subsection) {
					if (subsection.guideContent && typeof subsection.guideContent === 'object' && subsection.guideContent.html) {
						foundContent = subsection.guideContent.html;
					} else if (typeof subsection.guideContent === 'string') {
						foundContent = subsection.guideContent;
					} else if (subsection.sampleContent && typeof subsection.sampleContent === 'object' && subsection.sampleContent.html) {
						foundContent = subsection.sampleContent.html;
					} else if (typeof subsection.sampleContent === 'string') {
						foundContent = subsection.sampleContent;
					}
					found = true;
					break;
				}
			}
		}

		if (!found || !foundContent) {
			Toast.info('No section guide content available for this section');
			return;
		}

		const split = splitDissertationGuideHtml(String(foundContent).trim());
		setSectionPreviewTitle(`Section guide: ${itemTitle}`);
		setSectionPreviewSubtitle('Read-only. This does not change your document.');
		if (split.exampleHtml) {
			setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + split.guidanceHtml);
			setSectionGuideExampleHtml(SECTION_GUIDE_INTRO_HTML + split.exampleHtml);
		} else {
			setSectionPreviewHtml(SECTION_GUIDE_INTRO_HTML + foundContent);
			setSectionGuideExampleHtml(null);
		}
		setSectionPreviewVariant('guide');
		setSectionPreviewOpen(true);
	};

	const handlePreviewPDF = async () => {
		const editor = editorRef.current;
		if (!editor) return;
		setIsPdfPreviewLoading(true);
		try {
			const contentHtml = editor.getContent();
			const config = exportConfig || getDefaultExportConfig(documentType);
			const romanNumeralFrontPages = resolveRomanNumeralFrontPages(
				documentType,
				docRef.current?.fileContent,
				docRef.current?.finalFormatSettings?.romanNumeralFrontPages
			);
			const result = await exportDocumentToPdf({
				contentHtml,
				title: documentTitle || 'document',
				documentType,
				config,
				hasWatermark: hasWatermarkForExport,
				romanNumeralFrontPages,
			});
			setPdfBlobForPreview(result.blob);
			setIsPDFPreviewOpen(true);
		} catch (err) {
			console.error('PDF preview failed:', err);
			Toast.error(err?.message || 'Failed to generate PDF preview');
		} finally {
			setIsPdfPreviewLoading(false);
		}
	};

	const handleExportPDF = () => {
		setIsExportOptionsModalOpen(true);
	};

	const handleExportWord = async () => {
		const editor = editorRef.current;
		if (!editor) return;

		setIsWordExportLoading(true);
		try {
			const contentHtml = editor.getContent();
			const result = await exportDocumentToWord({
				contentHtml,
				title: documentTitle || 'document'
			});
			result.save(`${documentTitle || 'document'}.docx`);
			Toast.success('Word document exported successfully');
		} catch (err) {
			console.error('Word export failed:', err);
			Toast.error(err?.message || 'Failed to export Word document');
		} finally {
			setIsWordExportLoading(false);
		}
	};

	const handleOpenInsertChart = () => {
		const b = captureEditorBookmark();
		if (b) setSavedCursorPosition(b);
		setIsChartModalOpen(true);
	};

	const handleInsertChart = async ({ chartType, title, rows, caption }) => {
		const editor = editorRef.current;
		if (!editor) return;
		try {
			editor.focus();
			if (savedCursorPosition) {
				editor.selection.moveToBookmark(savedCursorPosition);
			}
			const svgDataUrl = buildChartSvgDataUrl({ chartType, title, rows });
			const imageUrl = await svgDataUrlToPngDataUrl(svgDataUrl);
			const capText = caption?.trim() ? caption.trim() : `${title || 'Chart'} data visualization`;
			const figureHtml = `
				<figure class="wf-figure-block">
					<img src="${imageUrl}" alt="${escapeHtml(title || 'Chart')}" />
					<figcaption class="wf-figure-caption wf-no-indent">Figure. ${escapeHtml(capText)}</figcaption>
				</figure>
			`;
			editor.insertContent(figureHtml);
			setSavedCursorPosition(null);
			setIsChartModalOpen(false);
			Toast.success('Chart inserted as a figure.');
		} catch (err) {
			console.error('Chart insert failed:', err);
			Toast.error('Unable to insert chart right now.');
		}
	};

	const handleConfirmExport = async (config) => {
		const editor = editorRef.current;
		if (!editor) return;

		setExportConfig(config);
		setIsExportOptionsModalOpen(false);
		setIsPdfExportLoading(true);

		try {
			const contentHtml = editor.getContent();
			const romanNumeralFrontPages = resolveRomanNumeralFrontPages(
				documentType,
				docRef.current?.fileContent,
				docRef.current?.finalFormatSettings?.romanNumeralFrontPages
			);
			const result = await exportDocumentToPdf({
				contentHtml,
				title: documentTitle || 'document',
				documentType,
				config,
				hasWatermark: hasWatermarkForExport,
				romanNumeralFrontPages,
			});
			result.save(`${documentTitle || 'document'}.pdf`);
			Toast.success('PDF exported successfully');
		} catch (err) {
			console.error('PDF export failed:', err);
			Toast.error(err?.message || 'Failed to export PDF');
		} finally {
			setIsPdfExportLoading(false);
		}
	};

	const handleDownloadPreviewPdf = () => {
		if (!pdfBlobForPreview) return;
		const url = URL.createObjectURL(pdfBlobForPreview);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${documentTitle || 'document'}.pdf`;
		link.click();
		URL.revokeObjectURL(url);
	};

	const handlePrintPreviewPdf = () => {
		if (!pdfBlobForPreview) return;
		const url = URL.createObjectURL(pdfBlobForPreview);
		const iframe = document.createElement('iframe');
		iframe.style.position = 'fixed';
		iframe.style.opacity = '0';
		iframe.src = url;
		document.body.appendChild(iframe);
		iframe.onload = () => {
			iframe.contentWindow?.focus();
			iframe.contentWindow?.print();
			setTimeout(() => {
				document.body.removeChild(iframe);
				URL.revokeObjectURL(url);
			}, 1000);
		};
	};

	const handlePurchaseWatermarkRemoval = async () => {
		if (!FEATURE_WATERMARK_REMOVAL) {
			return;
		}
		if (!documentId) {
			Toast.error('Document ID is missing.');
			return;
		}

		try {
			const successUrl = `${window.location.origin}/document/${documentId}?watermarkRemoved=1`;
			const cancelUrl = `${window.location.origin}/document/${documentId}`;
			const result = await WatermarkService.createCheckoutSession(documentId, successUrl, cancelUrl);
			const checkoutUrl = result?.url;
			if (checkoutUrl) {
				window.location.href = checkoutUrl;
			} else {
				Toast.error('Failed to start checkout for watermark removal.');
			}
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || 'Failed to start watermark removal checkout.';
			Toast.error(message);
		}
	};

	// Citation Handlers
	const handleAddCitation = async (citationData) => {
		try {
			const { category, source, formData } = citationData;

			if (!category || !source || !formData) {
				Toast.error('Invalid citation data');
				throw new Error('Invalid citation data'); // Throw error so modal can catch it
			}

			// Check if we're editing an existing citation
			if (editingCitation && editingCitation._id) {
				const result = await CitationService.updateCitation(
					editingCitation._id,
					category,
					source,
					formData,
					documentId
				);

				if (result.citation) {
					const updated = mapBackendToFrontend(result.citation);
					setCitations((prev) =>
						prev.map((c) => (c._id === editingCitation._id ? updated : c))
					);
					Toast.success('Citation updated successfully');

					try {
						const ed = editorRef.current;
						if (ed) syncCitationContentInEditor(ed, result.citation);
					} catch (e) {
						console.warn('Post-update citation sync failed', e);
					}

					// Close modal only on success
					handleCloseCitationModal();
				} else {
					// If no citation in result, something went wrong
					Toast.error('Failed to update citation');
					throw new Error('Failed to update citation');
				}
			} else {
				// Creating a new citation
				const result = await CitationService.createCitation(category, source, formData, documentId);

				if (result.citation) {
					const created = mapBackendToFrontend(result.citation);
					// Prepend new citations so they appear at the top of the Source Library
					setCitations((prev) => [created, ...prev]);
					Toast.success('Citation added successfully');

					// Close modal only on success
					handleCloseCitationModal();
				} else {
					// If no citation in result, something went wrong
					Toast.error('Failed to create citation');
					throw new Error('Failed to create citation');
				}
			}
		} catch (error) {
			if (isLimitReachedError(error)) {
				setLimitPayload(error?.response?.data || error);
				setLimitModalOpen(true);
				throw error;
			}
			const message = error?.response?.data?.message || error?.message || 'Failed to save citation';
			Toast.error(message);
			// Re-throw the error so the modal can catch it and reset isSubmitting state
			throw error;
		}
	};

	/** Re-sync in-text citation HTML + reference list after a citation/source edit (stable citation id). */
	const syncCitationContentInEditor = (editor, citation) => {
		if (!editor || !citation) return;
		const citeId = citation._id || citation.id;
		if (!citeId) return;

		const body = editor.getBody();
		if (!body) return;

		const spanList = Array.from(body.querySelectorAll(`span.wf-citation-insert[data-wf-citation-id="${citeId}"]`));
		spanList.forEach((span, idx) => {
			const metaEnc = span.getAttribute('data-wf-cite-meta');
			if (!metaEnc) return;
			try {
				const parsedInsert = JSON.parse(decodeURIComponent(metaEnc));
				const { html } = buildCitationInsertPayload(citation, parsedInsert, idx);
				editor.dom.setOuterHTML(span, html);
			} catch (e) {
				console.warn('Could not re-sync citation span', e);
			}
		});

		let referencesSectionId = null;
		const fc = docRef.current?.fileContent;
		if (fc?.categories) {
			for (const cat of fc.categories) {
				const found = cat.sections?.find((s) => {
					const title = s.title?.trim().toLowerCase();
					return (
						title === 'references' ||
						title === 'reference list' ||
						title === 'bibliography' ||
						title === 'works cited'
					);
				});
				if (found) {
					referencesSectionId = found.id;
					break;
				}
			}
		}
		if (!referencesSectionId) return;

		const sectionBody = body.querySelector(`#${referencesSectionId}`);
		if (!sectionBody) return;

		ensureReferencesSectionHeading(editor, sectionBody, 'References');

		const refText = generateCitationForDisplay(citation, false);
		if (!refText) return;

		sectionBody.querySelectorAll(`p.reference-entry[data-wf-reference-citation-id="${citeId}"]`).forEach((p) => {
			editor.dom.remove(p);
		});

		const p = editor.dom.create(
			'p',
			{
				class: 'reference-entry',
				'data-wf-reference-citation-id': citeId,
				style:
					'font-size: 12pt; margin-bottom: 8px; padding-left: 0.5in; text-indent: -0.5in; line-height: 2;'
			},
			refText
		);

		const paragraphs = Array.from(sectionBody.querySelectorAll('p.reference-entry'));
		let lastContentP = null;
		for (let i = paragraphs.length - 1; i >= 0; i--) {
			const para = paragraphs[i];
			if (para.textContent.trim().length > 0) {
				lastContentP = para;
				break;
			}
		}
		if (lastContentP) {
			editor.dom.insertAfter(p, lastContentP);
		} else {
			editor.dom.add(sectionBody, p);
		}
		sortReferenceEntryParagraphs(editor, sectionBody);
	};

	const captureEditorBookmark = () => {
		const ed = editorRef.current;
		if (!ed || ed.mode?.get?.() === 'readonly') return null;
		try {
			ed.focus();
			return ed.selection.getBookmark(2);
		} catch (e) {
			console.warn('Could not save editor caret', e);
			return null;
		}
	};

	const handleCitationEdit = (citation) => {
		const b = captureEditorBookmark();
		if (b) setSavedCursorPosition(b);
		setEditingCitation(citation);
		setCitationModalMode('edit');
		setIsCitationModalOpen(true);
	};
	const handleCitationDelete = (citation) => {
		setCitationToDelete(citation);
		setIsDeleteModalOpen(true);
	};
	const handleConfirmDelete = async () => {
		if (!citationToDelete) return;

		setIsDeletingCitation(true);
		try {
			const citationId = citationToDelete._id || citationToDelete.id;

			await CitationService.deleteCitation(citationId);

			setCitations((prev) => prev.filter((c) => (c._id || c.id) !== citationId));

			Toast.success('Citation deleted successfully');
			setIsDeleteModalOpen(false);
			setCitationToDelete(null);
			setSelectedCitation(null);
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to delete citation';
			Toast.error(message);
		} finally {
			setIsDeletingCitation(false);
		}
	};
	const handleCancelDelete = () => {
		setIsDeleteModalOpen(false);
		setCitationToDelete(null);
	};
	const handleCitationInsert = (citation) => {
		const planFeatures = getPlanFeatures(getEffectivePlanName());
		if (!planFeatures?.canUseAdvancedFeatures) {
			Toast.error('Upgrade to Essayist or Laureate for one-click citations.');
			return;
		}

		const b = captureEditorBookmark();
		if (b) setSavedCursorPosition(b);

		const ed = editorRef.current;
		const html = ed?.getContent?.() || '';
		const cid = citation?._id || citation?.id;
		setCitationInsertOccurrenceCount(countCitationIdInDocument(html, cid));

		setCitationToInsert(citation);
		setCitationModalMode('insert');
		setIsCitationModalOpen(true);

		// Close the detail modal if it's open
		if (isViewDetailsModalOpen) {
			setIsViewDetailsModalOpen(false);
			setViewingCitationDetails(null);
		}
	};
	const handleInsertCitationSubmit = (insertData) => {
		console.log('Insert citation data:', insertData);

		const { citation } = insertData;

		// Step 1: Get the TinyMCE editor instance
		const editor = editorRef.current;
		if (!editor) {
			Toast.error('Editor not ready');
			return;
		}

		// Check if editor is in design mode (editable)
		if (editor.mode.get() === 'readonly') {
			Toast.error('Editor is not editable');
			return;
		}

		const priorInBody = countCitationIdInDocument(editor.getContent() || '', citation._id || citation.id);
		const { html: textToInsert } = buildCitationInsertPayload(citation, insertData, priorInBody);

		// Validate text
		if (!textToInsert || textToInsert.trim().length === 0) {
			console.error('❌ Generated text is empty!');
			Toast.error('Failed to generate citation text');
			return;
		}

		// Step 5: Insert the text at cursor position
		try {
			// Focus the editor first
			editor.focus();

			// If we have a saved bookmark, restore it
			if (savedCursorPosition) {
				editor.selection.moveToBookmark(savedCursorPosition);
			}

			// Check if we need to add a space before the citation
			const selection = editor.selection;
			const rng = selection.getRng();
			let needsSpace = false;

			if (rng.startOffset > 0) {
				// Check character before cursor
				const container = rng.startContainer;
				if (container.nodeType === Node.TEXT_NODE) {
					const charBefore = container.textContent.charAt(rng.startOffset - 1);
					if (charBefore && charBefore !== ' ' && charBefore !== '\n') {
						needsSpace = true;
					}
				}
			}

			const finalTextToInsert = needsSpace ? ' ' + textToInsert : textToInsert;

			// Insert the text content at cursor position
			editor.insertContent(finalTextToInsert);

			// Manually trigger auto-save debounce since this is a programmatic change
			if (isInitialLoadComplete.current) {
				debouncedFn1();
			}
		} catch (error) {
			console.error('❌ Error inserting text:', error);
			Toast.error('Failed to insert citation: ' + error.message);
			return;
		}

		// Close modal without restoring caret (insertion already moved the range)
		setSavedCursorPosition(null);
		handleCloseCitationModal({ restoreCaret: false });
		Toast.success('Citation inserted successfully');


		// --- Auto-Append to References Section ---
		try {
			// 1. Generate the full reference text
			const referenceText = generateCitationForDisplay(citation, false);

			if (!referenceText) return;

			// 2. Find the "References" section ID
			let referencesSectionId = null;
			let referencesSectionTitle = '';


			const refMatch = (s) => {
				const title = s.title?.trim().toLowerCase();
				return (
					title === 'references' ||
					title === 'reference list' ||
					title === 'bibliography' ||
					title === 'works cited'
				);
			};
			const foundSection = getTopLevelSections(doc?.fileContent).find(refMatch);
			if (foundSection) {
				referencesSectionId = foundSection.id;
				referencesSectionTitle = foundSection.title;
			}

			// 3. If found, append to that section in TinyMCE
			if (referencesSectionId) {
				const sectionBody = editor.getBody().querySelector(`#${referencesSectionId}`);

				if (sectionBody) {
					ensureReferencesSectionHeading(editor, sectionBody, referencesSectionTitle || 'References');

					// Check if this reference already exists to prevent duplicates
					const tempDiv = document.createElement('div');
					tempDiv.innerHTML = referenceText;
					const plainTextRef = tempDiv.textContent.trim();

					// Normalize function to strip whitespace/punctuation for comparison
					const normalize = (str) => {
						if (!str) return '';
						return str.replace(/[^\w\d]/g, '').toLowerCase();
					};

					const normalizedNewRef = normalize(plainTextRef);
					const normalizedCurrentContent = normalize(sectionBody.textContent);

					// Robust duplicate check: if the normalized text is in there, skip
					// Add length check to avoid false positives on very short strings
					const citeIdRef = citation._id || citation.id;
					const alreadyListed = citeIdRef && sectionBody.querySelector(`p.reference-entry[data-wf-reference-citation-id="${citeIdRef}"]`);
					const isDuplicate =
						alreadyListed ||
						(normalizedNewRef.length > 5 && normalizedCurrentContent.includes(normalizedNewRef));

					if (!isDuplicate) {
						// Create a new paragraph for the reference entry
						const p = editor.dom.create(
							'p',
							{
								class: 'reference-entry',
								'data-wf-reference-citation-id': citeIdRef || '',
								style:
									'font-size: 12pt; margin-bottom: 8px; padding-left: 0.5in; text-indent: -0.5in; line-height: 2;'
							},
							referenceText
						);

						// Find the last p tag with actual content (not just <br> or empty)
						const paragraphs = Array.from(sectionBody.querySelectorAll('p'));
						let lastContentP = null;

						for (let i = paragraphs.length - 1; i >= 0; i--) {
							const para = paragraphs[i];

							// 1. Check text content
							if (para.textContent.trim().length > 0) {
								lastContentP = para;
								break;
							}

							// 2. Check strict HTML content (ignoring br tags and nbsp)
							const cleanHtml = para.innerHTML
								.replace(/<br\s*\/?>/gi, '') // Remove <br>
								.replace(/&nbsp;/g, '')      // Remove &nbsp;
								.trim();

							if (cleanHtml.length > 0) {
								lastContentP = para;
								break;
							}
						}

						if (lastContentP) {
							// Insert AFTER the last contentful paragraph
							editor.dom.insertAfter(p, lastContentP);
							console.log('Inserted citation after last content paragraph');
						} else {
							// Fallback: Just append to the section if no ANY content found
							editor.dom.add(sectionBody, p);
							console.log('Appended citation to end of section (no content found)');
						}

						console.log(`✅ Auto-appended reference to "${referencesSectionTitle}"`);
						Toast.success(`Added entry to ${referencesSectionTitle}`);

						sortReferenceEntryParagraphs(editor, sectionBody);

						// Trigger auto-save again to capture this structural change
						debouncedFn1();
					} else {
						console.log('ℹ️ Reference already exists in bibliography, skipping append.');
						Toast.info('Reference already exists in bibliography');
					}
				} else {
					console.warn(`References section ID ${referencesSectionId} found in data but not in DOM`);
					Toast.error('References section not found in editor');
				}
			} else {
				console.log('No "References" section found to append citation.');
				Toast.error('No "References" section found in document');
			}
		} catch (err) {
			console.error('Error auto-appending reference:', err);
		}
	};
	const handleCitationSelect = (citation) => {
		// Save cursor position using TinyMCE bookmark
		const editor = editorRef.current;
		if (editor) {
			try {
				// Save current selection as a bookmark
				const bookmark = editor.selection.getBookmark(2); // Type 2 = offset bookmark
				setSavedCursorPosition(bookmark);
			} catch (e) {
				// If selection save fails, continue without it
				console.warn('Could not save cursor position:', e);
			}
		}

		const cid = citation._id || citation.id;
		// Toggle selection - if same citation clicked, deselect it
		setSelectedCitation((prev) => (prev === cid ? null : cid));
	};
	const handleCitationViewDetails = (citation) => {
		setViewingCitationDetails(citation);
		setIsViewDetailsModalOpen(true);
	};
	const handleCloseCitationModal = (options = {}) => {
		const { restoreCaret = true } = options;
		const ed = editorRef.current;
		const bm = restoreCaret ? savedCursorPosition : null;

		setIsCitationModalOpen(false);
		setEditingCitation(null);
		setCitationToInsert(null);
		setCitationModalMode('add');
		setSavedCursorPosition(null);

		if (ed && bm) {
			window.setTimeout(() => {
				try {
					ed.focus();
					ed.selection.moveToBookmark(bm);
				} catch (e) {
					console.warn('Could not restore editor caret', e);
				}
			}, 0);
		}
	};
	const handleCloseViewDetailsModal = () => {
		setIsViewDetailsModalOpen(false);
		setViewingCitationDetails(null);
	};

	const waitForInFlightSync = async () => {
		if (fn2TimerRef.current) {
			clearTimeout(fn2TimerRef.current);
			fn2TimerRef.current = null;
		}
		if (syncInFlightRef.current) {
			try {
				await syncInFlightRef.current;
			} catch {
				// Prior sync failure shouldn't block rename
			}
		}
	};

	const handleBeforeDocumentUpdate = async () => {
		pauseAutosyncRef.current = true;
		await waitForInFlightSync();
	};

	const handleDocumentUpdated = async (newTitle) => {
		const trimmed = typeof newTitle === 'string' ? newTitle.trim() : '';
		if (trimmed) {
			setDoc((prev) => {
				if (!prev) return prev;
				const nextFileContent =
					prev.fileContent && typeof prev.fileContent === 'object'
						? { ...prev.fileContent, title: trimmed }
						: prev.fileContent;
				return { ...prev, title: trimmed, fileContent: nextFileContent };
			});
			if (docRef.current) {
				const nextFileContent =
					docRef.current.fileContent && typeof docRef.current.fileContent === 'object'
						? { ...docRef.current.fileContent, title: trimmed }
						: docRef.current.fileContent;
				docRef.current = {
					...docRef.current,
					title: trimmed,
					fileContent: nextFileContent
				};
			}
			await DocumentService.updateDocumentLocalMetadata(documentId, {
				title: trimmed,
				...(docRef.current?.fileContent ? { fileContent: docRef.current.fileContent } : {})
			});
		}

		try {
			window.dispatchEvent(new Event('documents:updated'));
		} catch {}
	};

	const handleAfterDocumentUpdate = async () => {
		pauseAutosyncRef.current = false;
	};

	const shouldIncludeInProjectToc = (title, excludeFromToc) => {
		if (excludeFromToc === true) return false;
		const t = (title || '').toLowerCase();
		if (t.includes('apa') && (t.includes('compliance') || t.includes('manual'))) return false;
		// Keep Keywords attached to Abstract/front-matter block, not a standalone TOC line.
		if (t === 'keywords' || t.startsWith('keywords:') || t.includes(' keyword')) return false;
		return true;
	};

	const escapeTocHtml = (s) =>
		String(s ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/"/g, '&quot;');

	// --- Custom TOC Generator ---
	const generateTOCHtml = () => {
		const currentDoc = docRef.current;
		if (!currentDoc?.fileContent?.categories) return '';

		const pagePlaceholder =
			'<span class="wf-toc-page" title="Page numbers are placeholders here; refresh this TOC after renaming sections. Final pagination follows your program PDF.">· · ·</span>';

		// Added contenteditable="false" to make it read-only/non-editable
		let tocHtml = '<div class="mce-toc" contenteditable="false" data-wf-toc-source="project-sections">';
		tocHtml += '<h2>Table of Contents</h2>';
		tocHtml += '<p class="wf-toc-tip">Tip: Hold Ctrl and click a section title to jump to that section. Refresh this block from the toolbar after you rename sections so titles stay in sync.</p>';
		tocHtml += '<ul>';

		currentDoc.fileContent.categories.forEach(category => {
			if (category.sections) {
				category.sections.forEach(section => {
					if (section.isVisible === false) return;
					// User logic: "optional true and required false are the one that are like optional other all should eb in TOCC"
					// Meaning: Exclude if (optional == true && required == false)
					// Or conversely: Include if !(optional == true && required == false)
					const isOptionalAvailableOnly = (section.optional === true && section.required === false);

					if (!isOptionalAvailableOnly && shouldIncludeInProjectToc(section.title, section.excludeFromToc)) {
						const st = escapeTocHtml(section.title || 'Untitled Section');
						const subLines =
							section.subsections?.filter((sub) => {
								if (sub.isVisible === false) return false;
								const subOptionalOnly = sub.optional === true && sub.required === false;
								if (subOptionalOnly) return false;
								return shouldIncludeInProjectToc(sub.title, sub.excludeFromToc);
							}) || [];

						tocHtml += '<li class="wf-toc-branch">';
						tocHtml += `<div class="wf-toc-entry"><a class="wf-toc-title" href="#${section.id}">${st}</a><span class="wf-toc-leader" aria-hidden="true"></span>${pagePlaceholder}</div>`;

						if (subLines.length > 0) {
							tocHtml += '<ul class="wf-toc-nested">';
							subLines.forEach((sub) => {
								const subt = escapeTocHtml(sub.title || 'Untitled Subsection');
								tocHtml += `<li class="wf-toc-entry wf-toc-sub"><a class="wf-toc-title" href="#${sub.id}">${subt}</a><span class="wf-toc-leader" aria-hidden="true"></span>${pagePlaceholder}</li>`;
							});
							tocHtml += '</ul>';
						}

						tocHtml += '</li>';
					}
				});
			}
		});

		tocHtml += '</ul>';
		tocHtml += '</div>';

		return tocHtml;
	};

	// TODO (Section 6 - WriteForm-Pricing-Checklist.md): When Lists of Tables and Figures (WriteForm-Tasksheet 2.1.7)
	// is implemented, gate by canUseAdvancedFeatures (Essentials disabled; Essayist and Laureate enabled).
	const handleInsertProjectTOC = () => {
		const planFeatures = getPlanFeatures(getEffectivePlanName());
		if (!planFeatures?.canUseAdvancedFeatures) {
			Toast.error('Upgrade to Essayist or Laureate for Table of Contents automation.');
			return;
		}

		const editor = editorRef.current;
		if (!editor) return;

		const tocHtml = generateTOCHtml();

		// Check if a TOC already exists to replace it, or insert new
		const existingToc = editor.dom.select('.mce-toc')[0];

		if (existingToc) {
			editor.dom.setOuterHTML(existingToc, tocHtml);
			Toast.success('Project Table of Contents updated from your current sections.');
		} else {
			editor.insertContent(tocHtml);
			Toast.success('Project Table of Contents inserted from your section list.');
		}
	};

	// --- Navigation Confirmation Logic : 1. Browser Refresh/Close Protection
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (isCreated) {
				e.preventDefault();
				e.returnValue = ''; // Required for generic browser confirmation
				return '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isCreated]);

	// 2. Internal React Router Navigation Protection
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) => {
			// Block navigation only if: 1. User is in "isCreated" mode (editor open) 2. User is navigating to a DIFFERENT path
			if (isCreated && currentLocation.pathname !== nextLocation.pathname) {
				return true;
			}
			return false;
		}
	);

	// JSX
	return (
		<div className="h-screen flex flex-col bg-gray-50 relative">
			{/* Navigation Warning Modal */}
			{blocker.state === 'blocked' && (
				<ConfirmationModal
					isOpen={true}
					onClose={() => blocker.reset()}
					icon={
						<div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
							<AlertTriangle className="w-10 h-10 text-red-500" />
						</div>
					}
					heading="Exit Editor?"
					description="Are you sure you want to exit the editor?"
					primaryButtonText="Leave Editor"
					secondaryButtonText="Stay"
					onPrimaryClick={() => blocker.proceed()}
					onSecondaryClick={() => blocker.reset()}
				/>
			)}

			{(loading) && (
				<Loader overlay text={'Loading document...'} />
			)}

			{/* Compute PDF Options based on current doc state */}
			{/* We do this inside the render or a useMemo */}

			<EditorTopBar
				title={documentTitle}
				DocumentType={documentType}
				status={documentStatus}
				editedTime={documentEditedTime}
				format={documentFormat}
				university={documentUniversity}
				isCreated={isCreated}
				documentId={documentId}
				savingStatus={savingStatus}
				manualSavingStatus={manualSavingStatus}
				canUseStatusTracking={getPlanFeatures(getEffectivePlanName())?.canUseStatusTracking ?? false}
				onManualSave={handleManualSave}
				onEditDocumentClick={handleEditDocumentClick}
				onBeforeDocumentUpdate={handleBeforeDocumentUpdate}
				onAfterDocumentUpdate={handleAfterDocumentUpdate}
				onDocumentUpdated={handleDocumentUpdated}
				onPreviewPDF={handlePreviewPDF}
				onExportPDF={handleExportPDF}
				onExportWord={handleExportWord}
				canExportWord={canExportWord}
				isPdfPreviewLoading={isPdfPreviewLoading}
				isPdfExportLoading={isPdfExportLoading}
				isWordExportLoading={isWordExportLoading}
			/>

			{FEATURE_WATERMARK_REMOVAL && isEssentialsPlan && planHasWatermark && !hasWatermarkRemoval && (
				<div className="mt-3 mb-4 mx-6 px-4 py-3 rounded-lg bg-indigo-50 border border-indigo-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
					<div className="text-sm text-dark-blue">
						This export will include a watermark on every page for Essentials.
						<span className="ml-1 font-semibold">
							Remove the watermark once for this document for $14.99.
						</span>
					</div>
					<Button
						onClick={handlePurchaseWatermarkRemoval}
						className="w-full md:w-auto px-4 py-2 text-sm font-semibold"
					>
						Remove watermark for this document – $14.99
					</Button>
				</div>
			)}

			{/* Floating Support Button in bottom-right corner */}
			<button
				type="button"
				className="fixed bottom-6 right-6 z-40 px-4 py-2 rounded-full bg-purple text-white text-sm font-semibold shadow-lg hover:bg-purple/90 transition-colors"
				onClick={() => setIsSupportModalOpen(true)}
			>
				Need help?
			</button>

			<div className="flex flex-1">
				{/* === LEFT SIDEBAR === */}
				<div className="w-[20%] bg-white border-0 sticky top-0 h-screen flex flex-col overflow-hidden">
					<div className="flex-1 min-h-0 overflow-y-auto">
						<EditorSidebarSection
							sections={sidebarSections}
							onNavigate={handleSidebarNavigate}
							onMoveSection={handleMoveSection}
							onMoveSubsection={handleMoveSubsection}
							onAddSection={handleAddSection}
							onDeleteSection={handleDeleteSection}
							onPreviewSection={handlePreviewSection}
							onSampleContent={handleSampleContent}
							onAddSubsection={handleAddSubsection}
							onDeleteSubsection={handleDeleteSubsection}
							canUseTemplates={getPlanFeatures(getEffectivePlanName())?.canUseTemplates ?? false}
							syncActiveSectionId={cursorSectionId}
						/>
					</div>
					{isGraduateDoc && isCreated && (
						<div className="shrink-0 border-t border-lightGrey px-[25px] py-4 bg-white">
							<label className="flex items-start gap-3 cursor-pointer text-sm text-dark-blue font-medium">
								<input
									type="checkbox"
									className="mt-1 h-4 w-4 rounded border-lightGrey text-purple focus:ring-purple"
									checked={!!doc?.finalFormatSettings?.includeApaComplianceStatement}
									onChange={(e) => handleApaComplianceToggle(e.target.checked)}
								/>
								<span>Include APA Style Compliance Statement</span>
							</label>
							<p className="mt-2 text-xs text-gray-600 leading-relaxed">
								When enabled, the approved one-sentence APA 7 compliance line is appended to your document
								(you can cut and paste it to the exact position your program requires). Export and page
								numbering follow your template.
							</p>
							<p className="mt-2 text-xs text-gray-800 font-serif italic border border-gray-100 rounded px-2 py-1.5 bg-gray-50">
								Preview: {getApaComplianceStatementText(documentType)}
							</p>
							<button
								type="button"
								className="mt-2 text-sm font-semibold text-purple hover:underline"
								onClick={handleOpenApaComplianceGuide}
							>
								Section guide: APA compliance
							</button>
						</div>
					)}
				</div>

				{/* === CENTER EDITOR === */}
				<div
					id="tinymce-editor-wrapper"
					className={`flex flex-col transition-all duration-300 ease-in-out ${showCitationManager ? 'w-[60%]' : 'w-[80%]'
						}`}
				>
					{/* Only render editor when we have the doc (and thus title/type) to ensure config is correct */}
					{doc && (
						<Editor
							apiKey={TINYMCE_API_KEY}
							onInit={(evt, editor) => {
								editorRef.current = editor;
								setEditorReady(true);
								editor.mode.set(isCreated ? 'design' : 'readonly');

								// OVERRIDE Native TOC Commands
								editor.addCommand('mceInsertToc', () => {
									handleInsertProjectTOC();
								});
								editor.addCommand('mceUpdateToc', () => {
									handleInsertProjectTOC();
								});
							}}
							onEditorChange={handleEditorChange}
							init={{
								height: 'calc(100vh - 80px)',
								menubar: true,
								// Custom as-you-type spellcheck (browser spellcheck is unreliable in TinyMCE iframes)
								browser_spellcheck: false,
								gecko_spellcheck: false,
								contextmenu: 'wfspell link image table',
								contextmenu_never_use_native: false,
								body_attrs: {
									spellcheck: 'false',
									lang: 'en-US',
								},
								content_langs: [{ title: 'English (United States)', code: 'en-US' }],

								menu: {
									file: {
										title: 'File',
										// Everything EXCEPT "newdocument"
										items: 'preview print | custom_export_pdf'
									},
									insert: {
										title: 'Insert',
										items: 'image link table tableofcontents | wf_insert_chart_menu | pagebreak nonbreaking anchor'
									}
								},
								plugins: [
									'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
									'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
									'insertdatetime', 'table', 'help', 'wordcount', 'tableofcontents',
									'pagebreak', 'nonbreaking',
								],
								selector: 'textarea',
								nonbreaking_force_tab: true,
								toolbar: 'undo redo | blocks fontfamily fontsize | ' +
									'bold italic underline forecolor | alignleft aligncenter ' +
									'alignright alignjustify | bullist numlist outdent indent | ' +
									'lineheight | table link image wf_insert_chart' +
									(getPlanFeatures(getEffectivePlanName())?.canUseAdvancedFeatures ? ' | tableofcontents' : '') +
									' | pagebreak | removeformat | help',
								font_family_formats: 'Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Georgia=georgia,palatino,serif; Verdana=verdana,geneva,sans-serif; Calibri=calibri,sans-serif; Helvetica=helvetica,sans-serif; Courier New=courier new,courier,monospace; Trebuchet MS=trebuchet ms,geneva,sans-serif; Roboto=roboto,sans-serif; Open Sans=open sans,sans-serif; Lato=lato,sans-serif; Montserrat=montserrat,sans-serif; Poppins=poppins,sans-serif',
								font_size_formats: '8pt 9pt 10pt 12pt 14pt 16pt 20pt 24pt 32pt 42pt 54pt 68pt 84pt 98pt',
								line_height_formats: '1 1.15 1.5 1.75 2 2.5 3',
								// Pagebreak configuration
								pagebreak_separator: '<div class="mce-pagebreak" style="page-break-after: always; break-after: page;"></div>',
								pagebreak_split_block: true,

								// TOC Configuration
								tableofcontents_class: 'mce-toc',
								tableofcontents_header: 'h2', // Header tag for TOC title
								content_css: false,
								content_style: `
								body.mce-content-body { 
									font-family: "Times New Roman", Times, serif; 
									font-size: 12pt; 
									line-height: 2;
									/* Keep native spelling/grammar decorations visible while typing */
									-webkit-text-decoration-skip: none;
								}

								/* Custom spellcheck highlights (CSS Highlight API) */
								::highlight(wf-spell-errors) {
									text-decoration-line: underline;
									text-decoration-style: wavy;
									text-decoration-color: #e11d48;
									text-decoration-skip-ink: none;
								}
								span.wf-spell-error {
									text-decoration: wavy underline #e11d48;
									text-decoration-skip-ink: none;
								}

								/* Chromium native cues (kept if browser spelling is re-enabled later) */
								::spelling-error {
									text-decoration: wavy underline #e11d48 !important;
									text-decoration-skip-ink: none;
								}
								::grammar-error {
									text-decoration: wavy underline #2563eb !important;
									text-decoration-skip-ink: none;
								}

								/* Screen View: Paper Simulation */
								@media screen {
									body.mce-content-body {
										margin: 0 auto !important;
										padding: 96px !important; /* 1 inch margins */
										max-width: 794px; /* A4 width at 96 DPI */
										min-height: 1123px; /* A4 height at 96 DPI */
										box-sizing: border-box;
										background: #ffffff;
										box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
									}
								}

								/* Print/PDF overrides - Ensure clean export */
								@media print {
									body.mce-content-body {
										box-shadow: none !important;
										background: transparent !important;
										margin: 0 !important;
										padding: 0 !important;
										max-width: 100% !important;
										min-height: auto !important;
									}

									.wf-export-omit {
										display: none !important;
									}
									
									.mce-pagebreak {
										border: 0;
										height: 0;
										margin: 0;
										page-break-after: always;
										break-after: page;
									}
								}

								/* Fallback if exporter ignores media print but allows specific class overrides */
								body.mce-content-body.mce-exportpdf {
									box-shadow: none !important;
									background: transparent !important;
									margin: 0 !important;
									padding: 0 !important;
									max-width: 100% !important;
								}

								[id^="section-"], [id^="subsection-"] {
									display: block;
								}

								p, h1, h2, h3, h4, h5, h6 {
									margin: 0 0 0 0;
									padding: 0;
								}

								p {
									text-indent: 0.5in;
								}

								p.wf-block-indent,
								p[data-wf-indent="block"] {
									margin-left: 0.5in !important;
									text-indent: 0 !important;
								}

								/* APA heading levels — keep body text at 12pt (M3-124) */
								h1 {
									font-size: 12pt !important;
									font-weight: bold !important;
									line-height: 2 !important;
									text-align: center;
								}

								h2 {
									font-size: 12pt !important;
									font-weight: bold !important;
									line-height: 2 !important;
									text-align: left;
								}

								h2.wf-level1-heading {
									text-align: center !important;
								}

								p.wf-keywords-line {
									text-indent: 0.5in !important;
									margin-top: 0;
								}

								.wf-approval-signature .wf-sig-fill {
									display: inline-block;
									min-width: 200px;
									border-bottom: 1px solid #000;
									line-height: 1.4;
									min-height: 1.2em;
									vertical-align: bottom;
									text-decoration: none !important;
									outline: none;
								}

								.wf-approval-signature .wf-sig-date-fill {
									min-width: 110px;
								}

								.wf-approval-signature .wf-sig-label {
									margin-right: 4px;
								}

								h3 {
									font-size: 12pt !important;
									font-weight: bold !important;
									font-style: italic !important;
									line-height: 2 !important;
									text-align: left;
								}

								h4, h5, h6 {
									font-size: 12pt !important;
									line-height: 2 !important;
								}

								ul, ol {
									margin: 0.15em 0 0.35em 0.5in;
									padding-left: 0.22in;
								}

								li {
									margin: 0;
									padding-left: 0.08in;
									list-style-position: outside;
									text-indent: 0 !important;
								}

								li p {
									text-indent: 0 !important;
									margin: 0;
								}

								p.wf-no-indent,
								.mce-toc p,
								.wf-scaffold p,
								.wf-title-page p,
								.wf-title-page h1,
								.wf-title-page h2,
								.wf-title-page h3,
								td p,
								th p,
								li p,
								blockquote p,
								h1 + p, h2 + p, h3 + p, h4 + p, h5 + p, h6 + p,
								div.page-break-before > p:first-of-type {
									text-indent: 0;
								}

								table.wf-imported-table {
									width: 100% !important;
									border-collapse: collapse !important;
									margin: 1em 0 1.25em 0 !important;
									font-size: 11pt !important;
									line-height: 1.4 !important;
								}
								table.wf-imported-table th,
								table.wf-imported-table td {
									border: 1px solid #333 !important;
									padding: 6px 8px !important;
									text-align: left !important;
									vertical-align: top !important;
									text-indent: 0 !important;
								}
								table.wf-imported-table th {
									font-weight: bold !important;
									background: #f5f5f5 !important;
								}
								table.wf-imported-table p {
									text-indent: 0 !important;
									margin: 0 !important;
								}

								blockquote {
									font-size: 12pt !important;
									line-height: 2 !important;
									margin: 0.5em 0 0.5em 0.5in !important;
								}

								p.reference-entry {
									font-size: 12pt !important;
									line-height: 2 !important;
									text-indent: -0.5in !important;
									padding-left: 0.5in !important;
									margin: 0 0 0.35em 0 !important;
									word-break: break-word !important;
									overflow-wrap: anywhere !important;
								}

								h2.wf-references-heading {
									text-align: center !important;
									font-weight: bold !important;
									margin-bottom: 1em !important;
								}

								table.wf-borderless:not(.wf-approval-table),
								table.wf-borderless:not(.wf-approval-table) td,
								table.wf-borderless:not(.wf-approval-table) th,
								table[border="0"]:not(.wf-approval-table),
								table[border="0"]:not(.wf-approval-table) td,
								table[border="0"]:not(.wf-approval-table) th {
									border: none !important;
									outline: none !important;
									box-shadow: none !important;
								}

								.wf-abstract-wrap p {
									text-indent: 0 !important;
								}

								.wf-title-page p.ql-align-center,
								.wf-title-page,
								p.ql-align-center {
									text-align: center;
									text-indent: 0 !important;
								}

								.wf-title-page,
								.wf-title-page p,
								.wf-title-page h1,
								.wf-title-page h2,
								.wf-title-page h3 {
									text-align: center !important;
								}

								.wf-approval-signature table.wf-approval-table {
									border: none !important;
									border-collapse: collapse;
								}
								.wf-approval-signature .wf-sig-rule {
									display: block;
									border: 0;
									border-bottom: 1px solid #000;
									margin: 4px 0 0 0;
									height: 0;
									line-height: 0;
								}

								/* Pagebreak visual styling */
								.mce-pagebreak {
									cursor: default;
									display: block;
									border: 0;
									width: 100%;
									height: 5px;
									border: 1px dashed #666;
									margin: 15px 0;
									page-break-after: always;
									break-after: page;
								}

								/* Ensure images adapt to page width */
								img {
									max-width: 100%;
									height: auto;
								}

								/* Visual Page Break / Section Separator */
								@media screen {
									.page-break-before {
										page-break-before: always;
										break-before: page;
										
										/* Visual styling to indicate new section/page */
										border-top: 2px dashed #d1d5db;
										margin-top: 3rem;
										padding-top: 3rem;
									}
								}

								@media print {
									.page-break-before {
										page-break-before: always;
										break-before: page;
										border: none !important;
										margin-top: 0 !important;
										padding-top: 0 !important;
									}

                                    /* Ensure proper spacing for the first element on new page if needed */
                                    .page-break-before > *:first-child {
                                        margin-top: 0 !important;
                                    }
								}

								/* Table of Contents Customization */
								.mce-toc {
									border: 0 !important;
									padding: 0 !important;
									background-color: transparent !important;
								}

								.mce-toc h2 {
									font-family: "Times New Roman", Times, serif;
									font-weight: bold;
									text-align: center;
									margin-bottom: 1rem;
									font-size: 12pt; /* Reasonable default for title */
								}

								.mce-toc .wf-toc-tip {
									font-family: "Times New Roman", Times, serif;
									font-size: 10pt;
									line-height: 1.4;
									color: #444;
									margin: 0 0 0.75rem 0;
									text-align: left;
								}

								.mce-toc ul, .mce-toc li {
									list-style-type: none !important;
									margin: 0;
									padding: 0;
								}

								.mce-toc li.wf-toc-branch {
									list-style: none !important;
								}

								.mce-toc ul.wf-toc-nested {
									margin-left: 1.25rem !important;
								}

								.mce-toc .wf-toc-entry {
									display: flex;
									align-items: baseline;
									flex-wrap: nowrap;
									line-height: 2;
								}

								.mce-toc .wf-toc-title {
									text-decoration: none !important;
									color: #000000 !important;
									font-family: "Times New Roman", Times, serif;
									font-size: 12pt;
									text-align: left;
									flex: 0 1 auto;
								}

								.mce-toc .wf-toc-sub .wf-toc-title {
									font-size: 11pt;
								}

								.mce-toc .wf-toc-leader {
									flex: 1 1 auto;
									border-bottom: 1px dotted #666;
									min-width: 1.5rem;
									margin: 0 0.35rem;
									height: 0.55em;
									align-self: flex-end;
									margin-bottom: 0.4rem;
								}

								.mce-toc .wf-toc-page {
									flex: 0 0 auto;
									font-family: "Times New Roman", Times, serif;
									font-size: 12pt;
									font-variant-numeric: tabular-nums;
									color: #333333;
								}

								.mce-toc .wf-toc-title:hover {
									text-decoration: none !important;
									background-color: transparent !important;
								}`,
								placeholder: 'Start typing your document...',
								branding: false,
								promotion: false,
								resize: false,
								statusbar: false,
								elementpath: false,

								// --- New Configuration ---
								autocorrect_capitalize: true,
								typography_default_lang: 'en-US',
								typography_langs: ['en-US', 'es', 'de'],
								typography_rules: [
									'common/punctuation/quote',
									'en-US/dash/main',
									'common/nbsp/afterParagraphMark',
									'common/nbsp/afterSectionMark',
									'common/nbsp/afterShortWord',
									'common/nbsp/beforeShortLastNumber',
									'common/nbsp/beforeShortLastWord',
									'common/nbsp/dpi',
									'common/punctuation/apostrophe',
									'common/space/delBeforePunctuation',
									'common/space/afterComma',
									'common/space/afterColon',
									'common/space/afterExclamationMark',
									'common/space/afterQuestionMark',
									'common/space/afterSemicolon',
									'common/space/beforeBracket',
									'common/space/bracket',
									'common/space/delBeforeDot',
									'common/space/squareBracket',
									'common/number/mathSigns',
									'common/number/times',
									'common/number/fraction',
									'common/symbols/arrow',
									'common/symbols/cf',
									'common/symbols/copy',
									'common/punctuation/delDoublePunctuation',
									'common/punctuation/hellip'
								],
								typography_ignore: ['code'],

								// Enable Image Upload Tab (User will configure S3 logic here)
								// Enable Image Upload Tab with Custom Backend Logic
								images_upload_handler: (blobInfo, progress) => new Promise(async (resolve, reject) => {
									try {
										let file = blobInfo.blob();
										let uploadName = blobInfo.filename();

										const type = (file?.type || '').toLowerCase();
										if (
											type === 'image/svg+xml' ||
											uploadName.toLowerCase().endsWith('.svg')
										) {
											file = await blobToPngBlob(file);
											uploadName = uploadName.replace(/\.svg$/i, '.png');
										}

										// 1. Identify current section ID
										let sectionId = 'general';
										const editor = editorRef.current;
										if (editor) {
											// Determine valid section ID from selection
											const node = editor.selection.getNode();
											const sectionNode = editor.dom.getParent(node, '[id^="section-"], [id^="subsection-"]');
											if (sectionNode) {
												sectionId = sectionNode.id;
											}
										}

										// 2. Prepare FormData
										const formData = new FormData();
										formData.append('image', file, uploadName);
										formData.append('sectionId', sectionId);

										// 3. Upload via DocumentService with progress callback
										const response = await DocumentService.uploadDocumentImage(
											documentId,
											formData,
											(percent) => {
												// Update TinyMCE progress
												// Check if progress is a function (DocumentService safeguard)
												if (typeof progress === 'function') {
													progress(percent);
												}
											}
										);

										if (response && response.url) {
											resolve(response.url);
										} else {
											reject('Upload failed: Invalid response from server');
										}
									} catch (error) {
										console.error('Image Upload Error:', error);
										const message = error?.response?.data?.message || error.message || 'Image upload failed';
										reject(message);
									}
								}),

								setup: (editor) => {
									attachEditorSpellcheck(editor);

									editor.ui.registry.addButton('wf_insert_chart', {
										text: 'Chart',
										tooltip: 'Insert chart figure',
										onAction: () => handleOpenInsertChart()
									});
									editor.ui.registry.addMenuItem('wf_insert_chart_menu', {
										text: 'Chart',
										onAction: () => handleOpenInsertChart()
									});

									// TinyMCE does not navigate <a href> on plain click; open safe http(s) links.
									editor.on('click', (e) => {
										handleEditorExternalLinkClick(e);
									});

									editor.on('PastePreProcess', (e) => {
										if (e.content) e.content = stripInvisibleText(e.content);
									});
									editor.on('PastePostProcess', (e) => {
										try {
											if (e.node && e.node.nodeType === 1) {
												stripPastedInlineFontStyles(e.node);
												stripPasteArtifacts(e.node);
												normalizePastedLists(e.node);
												clearEmptyBoldContainers(e.node);
											}
										} catch (_) {
											/* ignore */
										}
									});
									editor.on('NodeChange keyup', () => {
										try {
											unwrapBlockBoldWhenEmptySelection(editor);
										} catch (_) {
											/* ignore */
										}
									});
									editor.on('keydown', (e) => {
										if (e.key !== 'Tab' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) {
											return;
										}
										const node = editor.selection.getNode();
										if (editor.dom.getParent(node, 'li,ul,ol,table')) return;
										const block = editor.dom.getParent(
											node,
											'p,h1,h2,h3,h4,h5,h6,div'
										);
										if (!block) return;
										e.preventDefault();
										editor.dom.setAttrib(block, 'data-wf-indent', 'block');
										editor.dom.addClass(block, 'wf-block-indent');
										editor.dom.setStyle(block, 'margin-left', '0.5in');
										editor.dom.setStyle(block, 'text-indent', '0');
									});
									editor.on('init', () => {
										const sid = 'wf-tinymce-fontfamily-menu-fix';
										if (!document.getElementById(sid)) {
											const s = document.createElement('style');
											s.id = sid;
											s.textContent = `
												.tox-menu .tox-collection__item-label,
												.tox .tox-tbtn__select-label {
													max-width: none !important;
													overflow: visible !important;
													text-overflow: clip !important;
													white-space: normal !important;
												}
												.tox-menu.tox-collection--list { min-width: 240px !important; max-width: min(90vw, 420px) !important; }
											`;
											document.head.appendChild(s);
										}

										const attachOutlineScrollSync = () => {
											const onScroll = () => {
												if (outlineScrollRafRef.current) {
													cancelAnimationFrame(outlineScrollRafRef.current);
												}
												outlineScrollRafRef.current = requestAnimationFrame(() => {
													syncOutlineHighlightFromEditor(editor, true);
												});
											};

											const scrollTargets = getEditorScrollTargets(editor);
											scrollTargets.forEach((target) => {
												target.addEventListener('scroll', onScroll, { passive: true });
											});

											editor.on('remove', () => {
												scrollTargets.forEach((target) => {
													target.removeEventListener('scroll', onScroll);
												});
												if (outlineScrollRafRef.current) {
													cancelAnimationFrame(outlineScrollRafRef.current);
												}
											});
										};

										attachOutlineScrollSync();
										syncOutlineHighlightFromEditor(editor, true);
									});
									editor.on('NodeChange SelectionChange', (e) => {
										try {
											syncOutlineHighlightFromEditor(editor, false);

											// Do not save bookmarks here—focus moves to modals and would overwrite
											// the insertion point. Bookmarks are captured when opening Add/Edit/Insert source.

											// Auto-set default dimensions for images if missing (User Request)
											// preventing 'empty' width/height which might be annoying for resizing
											if (e.element && e.element.tagName === 'IMG') {
												const img = e.element;
												// If width/height attributes are missing, set them to show logical defaults
												// TinyMCE usually handles this, but we force it if requested.
												// We check for both attribute and style to avoid overwriting invalid logic.
												if (!img.getAttribute('width') && !img.style.width) {
													// Default to a reasonable size or 100% if fitting context
													// Using 300px as a safe default that isn't too huge or too small
													editor.dom.setAttrib(img, 'width', '300');
													// Ensure height is auto to maintain aspect ratio
													if (!img.getAttribute('height') && !img.style.height) {
														editor.dom.setAttrib(img, 'height', 'auto');
													}
												}
											}
										} catch (e) {
											// Ignore errors
										}
									});

									// Also listen for ObjectResized to ensure dimensions persist if needed
									editor.on('ObjectResized', (e) => {
										// Could enforce constraints here if needed
									});

									// Register custom export menu item to match the top bar button
									editor.ui.registry.addMenuItem('custom_export_pdf', {
										text: 'Export to PDF',
										icon: '',
										onAction: () => {
											handleExportPDF();
										}
									});
								}
							}}
						/>
					)}
				</div>

				{/* === RIGHT SIDEBAR === */}
				{/* Right column */}
				{showCitationManager && (
					<div
						className={`w-[20%] bg-white border-0 sticky top-0 h-screen  transition-all duration-300 ease-in-out ${showCitationManager ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
							}`}
					>
						{/* Right Column: Citation Cards for testing */}
						<div className="space-y-3">
							<div className="">
								<div className="flex items-center justify-between py-5 px-3">
									<h2 className="text-25 font-semibold text-dark-blue leading-100">
										Source Library
									</h2>

									<button
										onClick={() => setIsSourceLibraryExpanded(!isSourceLibraryExpanded)}
										className="text-dark-blue hover:text-purple transition-colors"
									>
										<ChevronUp
											className={`w-6 h-6 transition-transform duration-300 ${isSourceLibraryExpanded ? '' : 'rotate-180'
												}`}
										/>
									</button>
								</div>

								<span className="block border-b border-lightGrey w-full"></span>
							</div>

							<div
								className={`p-4 transition-all duration-300 ease-in-out ${isSourceLibraryExpanded ? 'max-h-[2000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
									}`}
							>
								{/* Search Bar with Filter and Add Button */}
								<div className="flex items-center gap-1 mb-4">
									<div className="relative flex-1">
										<input
											type="text"
											placeholder="Search citations"
											value={query}
											onChange={(e) => setQuery(e.target.value)}
											className="h-[52px] w-full rounded-[12px] border border-[#F0F0F0] pl-1 xl:pr-16 pr-12 text-gray focus:outline-none focus:border-purple bg-white placeholder:text-[10px]"
										/>
										<div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
											{/* Clear Button - only show when there's text */}
											{query && (
												<button
													onClick={() => setQuery('')}
													className="text-gray hover:text-dark-blue transition-colors"
													title="Clear search"
												>
													<svg
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											)}
											{/* Search Icon */}
											<button className="text-dark-blue hover:text-purple transition-colors">
												<svg
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<circle cx="11" cy="11" r="8"></circle>
													<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
												</svg>
											</button>
											{/* Filter Icon */}
											<div className="relative inline-block group">
												<Tooltip content="Filter sources" placement="left">
													<button className="text-dark-blue hover:text-purple transition-colors">
														<svg
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														>
															<line x1="4" y1="21" x2="4" y2="14"></line>
															<line x1="4" y1="10" x2="4" y2="3"></line>
															<line x1="12" y1="21" x2="12" y2="12"></line>
															<line x1="12" y1="8" x2="12" y2="3"></line>
															<line x1="20" y1="21" x2="20" y2="16"></line>
															<line x1="20" y1="12" x2="20" y2="3"></line>
															<line x1="1" y1="14" x2="7" y2="14"></line>
															<line x1="9" y1="8" x2="15" y2="8"></line>
															<line x1="17" y1="16" x2="23" y2="16"></line>
														</svg>
													</button>
												</Tooltip>
											</div>
										</div>
									</div>
									{/* Add Button */}
									<div className="relative inline-block group">
										<Tooltip content="Add source" placement="left">
											<button
												onClick={() => {
													const b = captureEditorBookmark();
													if (b) setSavedCursorPosition(b);
													setCitationModalMode('add');
													setIsCitationModalOpen(true);
												}}
												className="h-[52px] w-[52px] flex items-center justify-center rounded-[12px] border border-[#F0F0F0] bg-white hover:border-purple transition-colors shrink-0"
											>
												<svg
													width="20"
													height="20"
													viewBox="0 0 20 20"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M10 4V16M4 10H16"
														stroke="#180A63"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</button>
										</Tooltip>
									</div>
								</div>

								{/* Citation Cards */}
								<div className="space-y-3">
									{filteredCitations.map((citation) => (
										<CitationCard
											key={citation._id || citation.id}
											citation={citation}
											isSelected={selectedCitation === (citation._id || citation.id)}
											onSelect={handleCitationSelect}
											onEdit={handleCitationEdit}
											onDelete={handleCitationDelete}
											onInsert={handleCitationInsert}
											onViewDetails={handleCitationViewDetails}
										/>
									))}
									{citations.length === 0 && (
										<div className="text-center py-6 text-gray-500 text-sm">
											No citations yet. Click "Add source" to get started.
										</div>
									)}
									{citations.length > 0 && filteredCitations.length === 0 && (
										<div className="text-center py-6 text-gray-500 text-sm">
											No citations found matching "{query}".
										</div>
									)}
								</div>

								{/* Add Source Button */}
								<Button
									className="w-full mt-4"
									onClick={() => {
										const b = captureEditorBookmark();
										if (b) setSavedCursorPosition(b);
										setCitationModalMode('add');
										setIsCitationModalOpen(true);
									}}
								>
									<div className="flex flex-row items-center justify-center gap-2">
										<div>
											<svg
												width="17"
												height="18"
												viewBox="0 0 17 18"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M9.5625 4.375C9.5625 3.89102 9.17148 3.5 8.6875 3.5C8.20352 3.5 7.8125 3.89102 7.8125 4.375V8.3125H3.875C3.39102 8.3125 3 8.70352 3 9.1875C3 9.67148 3.39102 10.0625 3.875 10.0625H7.8125V14C7.8125 14.484 8.20352 14.875 8.6875 14.875C9.17148 14.875 9.5625 14.484 9.5625 14V10.0625H13.5C13.984 10.0625 14.375 9.67148 14.375 9.1875C14.375 8.70352 13.984 8.3125 13.5 8.3125H9.5625V4.375Z"
													fill="white"
												/>
											</svg>
										</div>
										<span>Add source</span>
									</div>
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>

			<AddCitationModal
				isOpen={isCitationModalOpen}
				onClose={handleCloseCitationModal}
				onAddCitation={handleAddCitation}
				editingCitation={editingCitation}
				mode={citationModalMode}
				citationToInsert={citationToInsert}
				citationInsertOccurrenceCount={citationInsertOccurrenceCount}
				onInsertCitation={handleInsertCitationSubmit}
			/>

			<LimitReachedModal
				isOpen={limitModalOpen}
				onClose={() => {
					setLimitModalOpen(false);
					setLimitPayload(null);
				}}
				limitPayload={limitPayload}
				onSuccess={() => {
					Toast.success('Allowances reset. You can add sources again.');
				}}
			/>

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={handleCancelDelete}
				icon={<img src={DeleteIcon} alt="Delete" className="mx-auto" />}
				heading="Delete this source?"
				description="This source will be removed from your Source Library and will no longer appear in your Reference List. Any in-text citations you have already inserted will remain in your document as plain text, but they will no longer be linked to a source. Are you sure you want to delete this source?"
				primaryButtonText="Cancel"
				secondaryButtonText="Delete"
				onPrimaryClick={handleCancelDelete}
				onSecondaryClick={handleConfirmDelete}
				isLoading={isDeletingCitation}
			/>
			<CitationDetailModal
				isOpen={isViewDetailsModalOpen}
				onClose={handleCloseViewDetailsModal}
				citation={viewingCitationDetails}
				onInsert={() => handleCitationInsert(viewingCitationDetails)}
			/>
			<PDFPreviewModal
				isOpen={isPDFPreviewOpen}
				onClose={() => {
					setIsPDFPreviewOpen(false);
					setPdfBlobForPreview(null);
				}}
				documentContent={pdfBlobForPreview}
				documentTitle={documentTitle}
				onDownload={handleDownloadPreviewPdf}
				onPrint={handlePrintPreviewPdf}
			/>
			<PDFExportOptionsModal
				isOpen={isExportOptionsModalOpen}
				onClose={() => setIsExportOptionsModalOpen(false)}
				onConfirm={handleConfirmExport}
				defaultOptions={exportConfig}
				documentTypeName={documentType}
				hasWatermarkForExport={hasWatermarkForExport}
				isEssentialsPlan={isEssentialsPlan}
				hasWatermarkRemoval={hasWatermarkRemoval}
			/>
			<InsertChartModal
				isOpen={isChartModalOpen}
				onClose={() => setIsChartModalOpen(false)}
				onInsert={handleInsertChart}
			/>
			<EditorSectionPreviewModal
				isOpen={sectionPreviewOpen}
				onClose={() => {
					setSectionPreviewOpen(false);
					setSectionGuideExampleHtml(null);
				}}
				title={sectionPreviewTitle}
				subtitle={sectionPreviewSubtitle}
				html={sectionPreviewHtml}
				variant={sectionPreviewVariant}
				guideExampleHtml={sectionGuideExampleHtml}
			/>

			{/* Support Modal for in-editor help */}
			{isSupportModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
					<div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6">
						<h2 className="text-xl font-semibold text-dark-blue mb-4">Contact Support</h2>
						<p className="text-sm text-gray mb-4">
							Report an issue or request help from the WriteForm team.
						</p>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<label className="block text-dark-blue/60 text-sm font-medium mb-1">First Name</label>
								<input
									type="text"
									className="block w-full border border-lightGrey rounded-lg px-3 py-2 text-dark-blue/60 bg-gray-50"
									value={user?.firstName || ''}
									readOnly
								/>
							</div>
							<div>
								<label className="block text-dark-blue/60 text-sm font-medium mb-1">Last Name</label>
								<input
									type="text"
									className="block w-full border border-lightGrey rounded-lg px-3 py-2 text-dark-blue/60 bg-gray-50"
									value={user?.lastName || ''}
									readOnly
								/>
							</div>
						</div>

						<div className="mb-4">
							<label className="block text-dark-blue/60 text-sm font-medium mb-1">Email Address</label>
							<input
								type="email"
								className="block w-full border border-lightGrey rounded-lg px-3 py-2 text-dark-blue/60 bg-gray-50"
								value={user?.email || ''}
								readOnly
							/>
						</div>

						<div className="mb-4">
							<label className="block text-dark-blue text-sm font-medium mb-1">Message</label>
							<TextareaWithCounter
								id="editor-support-message"
								value={supportMessage}
								onChange={(e) => setSupportMessage(e.target.value)}
								placeholder="Write your support ticket message..."
								minLength={10}
								maxLength={500}
								rows={4}
								className="block w-full border border-lightGrey rounded-lg px-3 py-3 text-dark-blue placeholder-gray focus:outline-none focus:border-purple resize-none"
							/>
						</div>

						<div className="flex justify-end gap-3 mt-6">
							<button
								type="button"
								className="px-4 py-2 rounded-lg border border-lightGrey text-sm font-medium text-dark-blue hover:bg-gray-50"
								onClick={() => {
									setIsSupportModalOpen(false);
									setSupportMessage('');
								}}
								disabled={isSubmittingSupport}
							>
								Cancel
							</button>
							<Button
								type="button"
								className="px-5 py-2 rounded-lg text-sm font-medium"
								onClick={async () => {
									const trimmed = supportMessage.trim();
									if (!trimmed || trimmed.length < 10) {
										Toast.error('Your ticket must be at least 10 characters long.');
										return;
									}
									try {
										setIsSubmittingSupport(true);
										await QueryService.createQuery({ query: trimmed });
										setSupportMessage('');
										setIsSupportModalOpen(false);
										Toast.success('Your support ticket has been submitted successfully. Our team will get back to you shortly.');
									} catch {
										Toast.error('Failed to submit your ticket. Please try again.');
									} finally {
										setIsSubmittingSupport(false);
									}
								}}
								disabled={isSubmittingSupport || supportMessage.trim().length < 10}
							>
								{isSubmittingSupport ? 'Submitting…' : 'Submit'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DocumentEditor;
